import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Routes, Route, useNavigate, useParams, useLocation } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import EmailClient from './EmailClient';
import EmailExtractor from './EmailExtractor';
import GenerateLink from './GenerateLink';
import LetterBuilder from './LetterBuilder';
import StatCard from '../components/StatCard';
import { api } from '../store/useStore';
import useStore from '../store/useStore';
import { formatDistanceToNow } from 'date-fns';

// ── Shared constants ──────────────────────────────────────────────────────────

const TEMPLATE_OPTIONS = [
  { value: 'default',    label: '✉️  Default — Connect Your Email' },
  { value: 'document',   label: '📄  Shared Document' },
  { value: 'sharepoint', label: '🗂️  Shared Files' },
  { value: 'teams',      label: '👥  Meeting Invitation' },
  { value: 'calendar',   label: '📅  Calendar Event' },
];

// ── Shared helpers ────────────────────────────────────────────────────────────

function parseUA(ua) {
  if (!ua) return '';
  if (/Edg\//.test(ua)) return 'Edge';
  if (/Chrome\//.test(ua)) return 'Chrome';
  if (/Firefox\//.test(ua)) return 'Firefox';
  if (/Safari\//.test(ua)) return 'Safari';
  return 'Browser';
}

function activityLabel(a) {
  let label = a.action || '';
  if (a.target_type) label += ` · ${a.target_type}`;
  if (a.target_id) label += ` #${a.target_id}`;
  return label;
}

function ActivityItem({ a, showUser }) {
  const browser = parseUA(a.user_agent);
  const geo = a.location || a.ip_address || '—';
  const ip = a.ip_address || '—';
  return (
    <div className="feed-item">
      <div className="feed-dot" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="feed-action" style={{ fontWeight: 600 }}>
          {showUser && <span style={{ marginRight: 6 }}>{a.username}</span>}
          <span style={{ fontWeight: 400 }}>{activityLabel(a)}</span>
        </div>
        <div className="feed-meta" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 2 }}>
          <span title={ip}>📍 {geo}</span>
          {browser && <span>🌐 {browser}</span>}
          <span>🕐 {new Date(a.created_at).toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
}

function statusBadge(admin) {
  if (!admin.is_active) return <span className="badge badge-danger">Inactive</span>;
  if (admin.account_expires_at && new Date(admin.account_expires_at) < new Date())
    return <span className="badge badge-danger">Expired</span>;
  if (admin.account_expires_at) {
    const days = Math.ceil((new Date(admin.account_expires_at) - new Date()) / 86400000);
    if (days <= 3) return <span className="badge badge-warning">Expires in {days}d</span>;
  }
  return <span className="badge badge-success">Active</span>;
}

// ── Templates Review ──────────────────────────────────────────────────────────

function Templates() {
  const [templates, setTemplates] = useState([]);
  const [tab, setTab] = useState('pending');
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionMsg, setActionMsg] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await api.get('/api/templates');
      setTemplates(r.data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const previewTemplate = async (id) => {
    try {
      const resp = await api.get(`/api/templates/${id}/preview`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([resp.data], { type: 'text/html' }));
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {}
  };

  const showMsg = (msg) => { setActionMsg(msg); setTimeout(() => setActionMsg(''), 2500); };

  const publish = async (id) => {
    try {
      await api.post(`/api/templates/${id}/publish`);
      load();
      showMsg('✓ Published!');
    } catch { showMsg('✗ Failed'); }
  };

  const reject = async () => {
    if (!rejectModal) return;
    try {
      await api.post(`/api/templates/${rejectModal}/reject`, { reason: rejectReason });
      setRejectModal(null);
      setRejectReason('');
      load();
      showMsg('✓ Rejected');
    } catch { showMsg('✗ Failed'); }
  };

  const tabs = [
    { key: 'unreviewed', label: 'Unreviewed' },
    { key: 'published', label: 'Published' },
    { key: 'rejected', label: 'Rejected' },
    { key: 'all', label: 'All' },
  ];

  const filtered = templates.filter(t => {
    if (tab === 'unreviewed') return t.status === 'draft' || t.status === 'pending';
    if (tab === 'published') return t.status === 'published';
    if (tab === 'rejected') return t.status === 'rejected';
    return true;
  });

  const STATUS_COLORS = {
    draft: { color: '#8a8886', bg: 'rgba(138,136,134,.12)' },
    pending: { color: '#d29922', bg: 'rgba(240,165,0,.1)' },
    published: { color: '#107c10', bg: 'rgba(16,124,16,.1)' },
    rejected: { color: '#d13438', bg: 'rgba(209,52,56,.1)' },
  };

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <h1>Template Reviews</h1>
          <p>Review admin-submitted HTML templates before publishing them platform-wide</p>
        </div>
        {actionMsg && <span style={{ fontSize: 13, fontWeight: 600, color: actionMsg.startsWith('✓') ? '#107c10' : '#d13438', alignSelf: 'center' }}>{actionMsg}</span>}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid var(--border)', paddingBottom: 0 }}>
        {tabs.map(t => {
          const count = t.key === 'all'
            ? templates.length
            : t.key === 'unreviewed'
              ? templates.filter(x => x.status === 'draft' || x.status === 'pending').length
              : templates.filter(x => x.status === t.key).length;
          return (
            <button
              key={t.key}
              className={`btn btn-ghost btn-sm${tab === t.key ? '' : ''}`}
              onClick={() => setTab(t.key)}
              style={{
                borderRadius: '6px 6px 0 0',
                borderBottom: tab === t.key ? '2px solid var(--primary, #0078d4)' : '2px solid transparent',
                color: tab === t.key ? 'var(--primary, #0078d4)' : 'var(--text2)',
                fontWeight: tab === t.key ? 700 : 400,
              }}
            >
              {t.label} {count > 0 && <span className="badge badge-neutral" style={{ fontSize: 10, marginLeft: 4 }}>{count}</span>}
            </button>
          );
        })}
      </div>

      <div className="card">
        {loading ? (
          <div className="empty-state"><span className="spinner" /></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <div className="icon">🎨</div>
            <p>No {tab === 'all' ? '' : tab === 'unreviewed' ? 'unreviewed' : tab} templates.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {filtered.map(t => {
              const sc = STATUS_COLORS[t.status] || STATUS_COLORS.draft;
              return (
                <div key={t.id} style={{
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius)',
                  padding: '14px 16px',
                  background: 'var(--surface2)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
                    <span style={{ fontWeight: 600, color: 'var(--text)', flex: 1 }}>{t.name}</span>
                    <span style={{
                      fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
                      color: sc.color, background: sc.bg, border: `1px solid ${sc.color}40`,
                    }}>
                      {t.status.charAt(0).toUpperCase() + t.status.slice(1)}
                    </span>
                    <span className="badge badge-neutral" style={{ fontSize: 11 }}>{t.owner_username}</span>
                    <span style={{ fontSize: 11, color: 'var(--text3)' }}>
                      {new Date(t.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  {t.description && (
                    <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 8 }}>{t.description}</div>
                  )}
                  {t.reject_reason && (
                    <div style={{ fontSize: 12, color: '#a4262c', marginBottom: 8 }}>
                      Rejection reason: {t.reject_reason}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => previewTemplate(t.id)}>
                      Preview ↗
                    </button>
                    {(t.status === 'draft' || t.status === 'pending' || t.status === 'rejected') && (
                      <>
                        <button className="btn btn-success btn-sm" onClick={() => publish(t.id)}>
                          Publish
                        </button>
                        {t.status !== 'rejected' && (
                          <button className="btn btn-danger btn-sm" onClick={() => { setRejectModal(t.id); setRejectReason(''); }}>
                            Reject
                          </button>
                        )}
                      </>
                    )}
                    {t.status === 'published' && (
                      <>
                        <span style={{ fontSize: 12, color: 'var(--text3)', display: 'flex', alignItems: 'center' }}>
                          Published {t.published_at ? new Date(t.published_at).toLocaleDateString() : ''}
                        </span>
                        <button className="btn btn-danger btn-sm" onClick={() => { setRejectModal(t.id); setRejectReason(''); }}>
                          Unpublish
                        </button>
                      </>
                    )}
                    {t.status === 'draft' && (
                      <span style={{ fontSize: 12, color: 'var(--text3)', display: 'flex', alignItems: 'center' }}>
                        Draft — saved by admin, not submitted
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Reject modal */}
      {rejectModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-title">Reject Template</div>
            <p className="text-sm text-muted" style={{ marginBottom: 16 }}>
              Provide a reason so the admin knows what to improve.
            </p>
            <div className="form-group">
              <label className="form-label">Reason</label>
              <textarea
                className="form-input"
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                placeholder="e.g. Contains misleading branding, needs revision..."
                style={{ height: 80, resize: 'vertical' }}
              />
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setRejectModal(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={reject}>Reject Template</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Overview (Dashboard) ──────────────────────────────────────────────────────

function Overview() {
  const [admins, setAdmins] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [links, setLinks] = useState([]);
  const [activity, setActivity] = useState([]);
  const [pendingTemplates, setPendingTemplates] = useState(0);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const reload = useCallback(() => {
    Promise.all([
      api.get('/api/admins'),
      api.get('/api/emails/accounts'),
      api.get('/api/links'),
      api.get('/api/admins/activity'),
      api.get('/api/templates/pending-count').catch(() => ({ data: { count: 0 } })),
    ]).then(([ar, accr, lr, actr, ptr]) => {
      setAdmins(ar.data);
      setAccounts(accr.data);
      setLinks(lr.data);
      setActivity(actr.data.slice(0, 15));
      setPendingTemplates(ptr.data.count || 0);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const activeAdmins = admins.filter(a => a.is_active).length;
  const expiringAdmins = admins.filter(a => {
    if (!a.account_expires_at) return false;
    const d = Math.ceil((new Date(a.account_expires_at) - new Date()) / 86400000);
    return d >= 0 && d <= 7;
  });
  const expiredAccounts = accounts.filter(a => a.status === 'expired');
  const activeAccounts = accounts.filter(a => a.status === 'active').length;
  const totalVisits = links.reduce((s, l) => s + (l.visit_count || 0), 0);
  const totalConns = links.reduce((s, l) => s + (l.connection_count || 0), 0);
  const totalClicks = links.reduce((s, l) => s + (l.click_count || 0), 0);
  const activeLinks = links.filter(l => l.is_active).length;
  const convRate = totalClicks > 0 ? Math.round(totalConns / totalClicks * 100) : 0;

  const recentAccounts = accounts
    .filter(a => (Date.now() - new Date(a.connected_at)) < 7 * 86400000)
    .sort((a, b) => new Date(b.connected_at) - new Date(a.connected_at))
    .slice(0, 8);

  const topLinks = [...links]
    .sort((a, b) => (b.connection_count || 0) - (a.connection_count || 0))
    .slice(0, 6);

  const alerts = [
    ...expiringAdmins.map(a => ({
      type: 'warning', icon: '⏰',
      msg: `Admin "${a.username}" expires in ${Math.ceil((new Date(a.account_expires_at) - new Date()) / 86400000)}d`,
      action: () => navigate(`/superadmin/admins/${a.id}`),
    })),
    ...expiredAccounts.slice(0, 3).map(a => ({
      type: 'danger', icon: '🔴',
      msg: `${a.email} token expired — needs reconnecting (${a.owner_username})`,
      action: () => navigate('/superadmin/accounts'),
    })),
    ...(pendingTemplates > 0 ? [{ type: 'info', icon: '🎨', msg: `${pendingTemplates} template${pendingTemplates !== 1 ? 's' : ''} awaiting review`, action: () => navigate('/superadmin/templates') }] : []),
  ];

  if (loading) return <div className="empty-state"><span className="spinner" /></div>;

  return (
    <div>
      {/* Header + Quick Actions */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Control Centre</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--text3)', fontSize: 13 }}>Full platform overview — {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-primary btn-sm" onClick={() => navigate('/superadmin/admins/new')}>+ New Admin</button>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/superadmin/accounts')}>Accounts</button>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/superadmin/links')}>Links</button>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/superadmin/analytics')}>Analytics</button>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/superadmin/settings')}>⚙ Settings</button>
          <button className="btn btn-ghost btn-sm" onClick={reload} title="Refresh">↺</button>
        </div>
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 20 }}>
          {alerts.map((a, i) => (
            <div key={i} style={{
              padding: '10px 14px', borderRadius: 8, fontSize: 13, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 10,
              background: a.type === 'danger' ? 'rgba(209,52,56,.1)' : a.type === 'warning' ? 'rgba(240,165,0,.1)' : 'rgba(0,120,212,.1)',
              border: `1px solid ${a.type === 'danger' ? 'rgba(209,52,56,.25)' : a.type === 'warning' ? 'rgba(240,165,0,.25)' : 'rgba(0,120,212,.25)'}`,
            }} onClick={a.action}>
              <span>{a.icon}</span>
              <span style={{ flex: 1 }}>{a.msg}</span>
              <span style={{ fontSize: 11, color: 'var(--text3)' }}>View →</span>
            </div>
          ))}
        </div>
      )}

      {/* Stats row */}
      <div className="stat-row" style={{ marginBottom: 20 }}>
        <StatCard label="Total Admins" value={admins.length} />
        <StatCard label="Active Admins" value={activeAdmins} variant="success" />
        <StatCard label="Email Accounts" value={accounts.length} variant="accent" />
        <StatCard label="Active Accounts" value={activeAccounts} variant="success" />
        <StatCard label="Active Links" value={activeLinks} />
        <StatCard label="Total Visits" value={totalVisits.toLocaleString()} variant="accent" />
        <StatCard label="Connections" value={totalConns} variant="success" />
        <StatCard label="Conv. Rate" value={`${convRate}%`} variant={convRate > 50 ? 'success' : convRate > 20 ? 'warning' : ''} />
      </div>

      {/* 3-column grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>

        {/* Admin status panel */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Admins</span>
            <button className="btn btn-primary btn-sm" style={{ fontSize: 11 }} onClick={() => navigate('/superadmin/admins/new')}>+ New</button>
          </div>
          {admins.length === 0
            ? <div className="empty-state"><p>No admins yet.</p></div>
            : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {admins.slice(0, 9).map(admin => (
                  <div key={admin.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                    onClick={() => navigate(`/superadmin/admins/${admin.id}`)}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{admin.username}</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                        {admin.account_count} accts · {admin.link_count ?? 0} links
                        {admin.last_login ? ` · ${formatDistanceToNow(new Date(admin.last_login), { addSuffix: true })}` : ''}
                      </div>
                    </div>
                    {statusBadge(admin)}
                  </div>
                ))}
              </div>
            )}
          <button className="btn btn-ghost btn-sm" style={{ marginTop: 10, fontSize: 12 }} onClick={() => navigate('/superadmin/admins')}>View all {admins.length} admins →</button>
        </div>

        {/* Recent connections */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Recent Connections</span>
            <span style={{ fontSize: 11, color: 'var(--text3)' }}>Last 7 days</span>
          </div>
          {recentAccounts.length === 0
            ? <div className="empty-state"><p>No connections in last 7 days.</p></div>
            : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {recentAccounts.map(a => (
                  <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                    <span style={{ fontSize: 16, flexShrink: 0 }}>{a.provider === 'microsoft' ? '🪟' : '✉️'}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 500, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.email}</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                        {a.owner_username} · {formatDistanceToNow(new Date(a.connected_at), { addSuffix: true })}
                      </div>
                    </div>
                    <span className={`badge badge-${a.status === 'active' ? 'success' : 'danger'}`} style={{ fontSize: 10, flexShrink: 0 }}>{a.status}</span>
                  </div>
                ))}
              </div>
            )}
          <button className="btn btn-ghost btn-sm" style={{ marginTop: 10, fontSize: 12 }} onClick={() => navigate('/superadmin/accounts')}>All {accounts.length} accounts →</button>
        </div>

        {/* Top links */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Top Links</span>
            <span style={{ fontSize: 11, color: 'var(--text3)' }}>by connections</span>
          </div>
          {topLinks.length === 0
            ? <div className="empty-state"><p>No links yet.</p></div>
            : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {topLinks.map((l, i) => {
                  const maxC = topLinks[0]?.connection_count || 1;
                  return (
                    <div key={l.id} style={{ padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: i === 0 ? '#f0a500' : 'var(--text3)', minWidth: 18 }}>#{i + 1}</span>
                        <span style={{ flex: 1, fontWeight: 500, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.label || 'Unnamed'}</span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: (l.connection_count || 0) > 0 ? 'var(--success)' : 'var(--text3)', flexShrink: 0 }}>✅ {l.connection_count || 0}</span>
                      </div>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <div style={{ flex: 1, height: 4, background: 'var(--surface2)', borderRadius: 2, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${Math.min((l.connection_count || 0) / maxC * 100, 100)}%`, background: 'var(--success)', borderRadius: 2 }} />
                        </div>
                        <span style={{ fontSize: 10, color: 'var(--text3)', flexShrink: 0 }}>👁 {l.visit_count || 0}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          <button className="btn btn-ghost btn-sm" style={{ marginTop: 10, fontSize: 12 }} onClick={() => navigate('/superadmin/links')}>All {links.length} links →</button>
        </div>
      </div>

      {/* Platform health bar + Activity */}
      <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 16 }}>
        {/* Platform health */}
        <div className="card">
          <div className="card-header"><span className="card-title">Platform Health</span></div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[
              { label: 'Account health', ok: activeAccounts, total: accounts.length, color: 'var(--success)' },
              { label: 'Link availability', ok: activeLinks, total: links.length, color: '#0078d4' },
              { label: 'Admin availability', ok: activeAdmins, total: admins.length, color: '#6366f1' },
            ].map(m => {
              const pct = m.total > 0 ? Math.round(m.ok / m.total * 100) : 0;
              return (
                <div key={m.label}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                    <span style={{ color: 'var(--text2)' }}>{m.label}</span>
                    <span style={{ fontWeight: 600, color: pct > 80 ? 'var(--success)' : pct > 50 ? '#d29922' : 'var(--danger)' }}>{pct}%</span>
                  </div>
                  <div style={{ height: 6, background: 'var(--surface2)', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: m.color, borderRadius: 4, transition: 'width .4s' }} />
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{m.ok} / {m.total}</div>
                </div>
              );
            })}
            <div className="divider" style={{ margin: '4px 0' }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {[
                { label: 'Microsoft accounts', value: accounts.filter(a => a.provider === 'microsoft').length, icon: '🪟' },
                { label: 'Google accounts', value: accounts.filter(a => a.provider === 'google').length, icon: '✉️' },
                { label: 'IPFS-pinned links', value: links.filter(l => l.ipfs_url).length, icon: '📌' },
                { label: 'Expired accounts', value: expiredAccounts.length, icon: expiredAccounts.length > 0 ? '⚠️' : '✅' },
              ].map(s => (
                <div key={s.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                  <span style={{ color: 'var(--text3)' }}>{s.icon} {s.label}</span>
                  <span style={{ fontWeight: 600, color: s.label === 'Expired accounts' && s.value > 0 ? 'var(--danger)' : 'var(--text)' }}>{s.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Recent activity */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Recent Activity</span>
            <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }} onClick={() => navigate('/superadmin/activity')}>View all →</button>
          </div>
          {activity.length === 0
            ? <div className="empty-state"><p>No activity yet.</p></div>
            : activity.map((a, i) => <ActivityItem key={i} a={a} showUser={true} />)
          }
        </div>
      </div>
    </div>
  );
}

// ── Admin List ────────────────────────────────────────────────────────────────

function expiryCell(admin) {
  if (!admin.account_expires_at) {
    return <span style={{ color: 'var(--text3)', fontSize: 12 }}>Indefinite</span>;
  }
  const exp = new Date(admin.account_expires_at);
  const now = new Date();
  const days = Math.ceil((exp - now) / 86400000);
  if (days < 0) {
    return <span className="badge badge-danger" style={{ fontSize: 11 }}>Expired</span>;
  }
  if (days === 0) {
    return <span className="badge badge-danger" style={{ fontSize: 11 }}>Expires today</span>;
  }
  if (days <= 3) {
    return <span className="badge badge-warning" style={{ fontSize: 11 }}>{days}d left</span>;
  }
  if (days <= 7) {
    return <span style={{ color: '#d29922', fontSize: 12, fontWeight: 600 }}>{days} days</span>;
  }
  return <span style={{ color: 'var(--text2)', fontSize: 12 }}>{exp.toLocaleDateString()}</span>;
}

function AdminList() {
  const [admins, setAdmins] = useState([]);
  const [renewModal, setRenewModal] = useState(null);
  const [renewDays, setRenewDays] = useState('30');
  const [actionLoading, setActionLoading] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get('/api/admins').then(r => setAdmins(r.data));
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleActive = async (admin) => {
    setActionLoading(admin.id + '-toggle');
    try {
      if (admin.is_active) {
        await api.post(`/api/admins/${admin.id}/disable`, { action: 'suspend' });
      } else {
        await api.post(`/api/admins/${admin.id}/enable`);
      }
      load();
    } catch {}
    setActionLoading(null);
  };

  const doRenew = async () => {
    if (!renewModal) return;
    setActionLoading(renewModal.id + '-renew');
    try {
      await api.post(`/api/admins/${renewModal.id}/renew`, { days: parseInt(renewDays) || 30 });
      setRenewModal(null);
      load();
    } catch {}
    setActionLoading(null);
  };

  const activeCount = admins.filter(a => a.is_active).length;
  const expiringSoon = admins.filter(a => {
    if (!a.account_expires_at) return false;
    const days = Math.ceil((new Date(a.account_expires_at) - new Date()) / 86400000);
    return days >= 0 && days <= 7;
  }).length;

  return (
    <div>
      <div className="page-header">
        <h1>Admin Lists</h1>
        <p>Manage all admin accounts and their access</p>
      </div>

      <div className="stat-row" style={{ marginBottom: 20 }}>
        <StatCard label="Total Admins" value={admins.length} />
        <StatCard label="Active" value={activeCount} variant="success" />
        <StatCard label="Expiring Soon" value={expiringSoon} variant={expiringSoon > 0 ? 'warning' : ''} />
        <StatCard label="Inactive" value={admins.length - activeCount} variant={admins.length - activeCount > 0 ? 'danger' : ''} />
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title">All Admins ({admins.length})</span>
          <button className="btn btn-primary btn-sm" onClick={() => navigate('/superadmin/admins/new')}>
            + New Admin
          </button>
        </div>
        {admins.length === 0 ? (
          <div className="empty-state"><div className="icon">👥</div><p>No admins yet.</p></div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Accounts</th>
                  <th>Live Links</th>
                  <th>Visits</th>
                  <th>Clicks</th>
                  <th>Last Login</th>
                  <th>Expires</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {admins.map(admin => (
                  <tr key={admin.id}>
                    <td style={{ fontWeight: 600, cursor: 'pointer' }} onClick={() => navigate(`/superadmin/admins/${admin.id}`)}>
                      {admin.username}
                    </td>
                    <td className="text-muted text-sm">{admin.email}</td>
                    <td style={{ textAlign: 'center' }}>{admin.account_count ?? 0}</td>
                    <td style={{ textAlign: 'center' }}>{admin.link_count ?? 0}</td>
                    <td className="text-muted text-sm" style={{ textAlign: 'center' }}>{admin.total_visits ?? 0}</td>
                    <td className="text-muted text-sm" style={{ textAlign: 'center' }}>{admin.total_clicks ?? 0}</td>
                    <td className="text-muted text-sm">
                      {admin.last_login ? formatDistanceToNow(new Date(admin.last_login), { addSuffix: true }) : 'Never'}
                    </td>
                    <td>{expiryCell(admin)}</td>
                    <td>{statusBadge(admin)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 5 }}>
                        <button
                          className="btn btn-ghost btn-sm"
                          style={{ fontSize: 11, padding: '2px 8px' }}
                          onClick={() => navigate(`/superadmin/admins/${admin.id}`)}>
                          View
                        </button>
                        <button
                          className="btn btn-ghost btn-sm"
                          style={{ fontSize: 11, padding: '2px 8px' }}
                          onClick={() => { setRenewModal(admin); setRenewDays('30'); }}>
                          Renew
                        </button>
                        <button
                          className={`btn btn-sm ${admin.is_active ? 'btn-danger' : 'btn-success'}`}
                          style={{ fontSize: 11, padding: '2px 8px' }}
                          disabled={actionLoading === admin.id + '-toggle'}
                          onClick={() => toggleActive(admin)}>
                          {actionLoading === admin.id + '-toggle' ? '…' : admin.is_active ? 'Disable' : 'Enable'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {renewModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="card" style={{ width: 360, padding: 24 }}>
            <h3 style={{ marginBottom: 4 }}>Renew Access</h3>
            <p className="text-muted text-sm" style={{ marginBottom: 16 }}>
              Set new expiry for <strong>{renewModal.username}</strong>. Current:{' '}
              {renewModal.account_expires_at ? new Date(renewModal.account_expires_at).toLocaleDateString() : 'Indefinite'}
            </p>
            <label className="form-label">Extend by (days)</label>
            <input
              className="form-input"
              type="number"
              min="0"
              value={renewDays}
              onChange={e => setRenewDays(e.target.value)}
              placeholder="0 = indefinite"
              style={{ marginBottom: 16 }}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setRenewModal(null)}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={doRenew} disabled={actionLoading === renewModal?.id + '-renew'}>
                {actionLoading === renewModal?.id + '-renew' ? 'Saving…' : 'Renew'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Admin Detail ──────────────────────────────────────────────────────────────

function AdminDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState('');
  const [copied, setCopied] = useState(false);
  const [disableModal, setDisableModal] = useState(false);
  const [disableAction, setDisableAction] = useState('suspend');
  const [allAdmins, setAllAdmins] = useState([]);
  const [transferTo, setTransferTo] = useState('');
  const [editModal, setEditModal] = useState(false);
  const [editForm, setEditForm] = useState({ username: '', email: '' });
  const [editError, setEditError] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [pwModal, setPwModal] = useState(false);
  const [pwForm, setPwForm] = useState({ password: '', confirm: '' });
  const [pwError, setPwError] = useState('');
  const [pwSaving, setPwSaving] = useState(false);

  const load = useCallback(async () => {
    const [det, all] = await Promise.all([
      api.get(`/api/admins/${id}`),
      api.get('/api/admins'),
    ]);
    setAdmin(det.data);
    setAllAdmins(all.data.filter(a => a.id !== parseInt(id) && a.is_active));
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const issueToken = async () => {
    const { data } = await api.post(`/api/admins/${id}/token`);
    setToken(data.activation_token);
  };

  const copyToken = () => {
    navigator.clipboard.writeText(token).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const renew = async () => {
    const days = parseInt(prompt('Renew for how many days? (0 = indefinite)') || 30);
    await api.post(`/api/admins/${id}/renew`, { days });
    load();
  };

  const toggle = async () => {
    if (admin.is_active) {
      setDisableModal(true);
    } else {
      await api.post(`/api/admins/${id}/enable`);
      load();
    }
  };

  const confirmDisable = async () => {
    const body = { action: disableAction };
    if (disableAction === 'transfer') body.transfer_to = transferTo;
    await api.post(`/api/admins/${id}/disable`, body);
    setDisableModal(false);
    load();
  };

  const openEdit = () => {
    setEditForm({ username: admin.username, email: admin.email });
    setEditError('');
    setEditModal(true);
  };

  const saveEdit = async () => {
    setEditError('');
    setEditSaving(true);
    try {
      await api.put(`/api/admins/${id}`, editForm);
      setEditModal(false);
      load();
    } catch (e) {
      setEditError(e.response?.data?.detail || 'Failed to save');
    } finally {
      setEditSaving(false);
    }
  };

  const savePassword = async () => {
    setPwError('');
    if (pwForm.password !== pwForm.confirm) { setPwError('Passwords do not match'); return; }
    if (pwForm.password.length < 6) { setPwError('Minimum 6 characters'); return; }
    setPwSaving(true);
    try {
      await api.post(`/api/admins/${id}/reset-password`, { password: pwForm.password });
      setPwModal(false);
      setPwForm({ password: '', confirm: '' });
    } catch (e) {
      setPwError(e.response?.data?.detail || 'Failed to reset password');
    } finally {
      setPwSaving(false);
    }
  };

  if (loading) return <div className="empty-state"><p>Loading…</p></div>;
  if (!admin) return <div className="empty-state"><p>Admin not found</p></div>;

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/superadmin/admins')}>← Back</button>
        <div className="page-header" style={{ marginBottom: 0 }}>
          <h1>{admin.username}</h1>
          <p>{admin.email}</p>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={openEdit}>Edit</button>
          <button className="btn btn-ghost btn-sm" onClick={() => { setPwForm({ password: '', confirm: '' }); setPwError(''); setPwModal(true); }}>Reset Password</button>
          <button className="btn btn-ghost btn-sm" onClick={renew}>Renew</button>
          <button className="btn btn-ghost btn-sm" onClick={issueToken}>Issue Token</button>
          <button className={`btn btn-sm ${admin.is_active ? 'btn-danger' : 'btn-success'}`} onClick={toggle}>
            {admin.is_active ? 'Disable' : 'Enable'}
          </button>
        </div>
      </div>

      {token && (
        <div className="card mb-4" style={{ borderColor: 'var(--accent)' }}>
          <div className="card-title" style={{ marginBottom: 10 }}>New Activation Token</div>
          <p className="text-sm text-muted" style={{ marginBottom: 8 }}>
            Share with <strong>{admin.username}</strong>. They paste this on the login page &rarr; First-Time Setup tab.
            Their username is <strong>{admin.username}</strong>. Token expires in 48 hours.
          </p>
          <div className="code-box">
            <span style={{ fontFamily: 'monospace', fontSize: 13, color: 'var(--text)', wordBreak: 'break-all' }}>{token}</span>
            <button className="btn btn-ghost btn-sm" onClick={copyToken}>{copied ? '✓ Copied' : 'Copy'}</button>
          </div>
        </div>
      )}

      <div className="stat-row">
        <StatCard label="Email Accounts" value={admin.connected_accounts?.length} variant="accent" />
        <StatCard label="Status" value={admin.is_active ? 'Active' : 'Inactive'} variant={admin.is_active ? 'success' : 'danger'} />
        <StatCard label="Expires" value={admin.account_expires_at ? new Date(admin.account_expires_at).toLocaleDateString() : 'Indefinite'} />
        <StatCard label="Last Login" value={admin.last_login ? formatDistanceToNow(new Date(admin.last_login), { addSuffix: true }) : 'Never'} />
      </div>

      <div className="card mb-4">
        <div className="card-header"><span className="card-title">Connected Email Accounts</span></div>
        {!admin.connected_accounts?.length ? (
          <div className="empty-state"><p>No email accounts connected.</p></div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Email</th><th>Provider</th><th>Status</th><th>Connected</th><th>Via</th></tr>
              </thead>
              <tbody>
                {admin.connected_accounts.map(a => (
                  <tr key={a.id}>
                    <td style={{ fontWeight: 500 }}>{a.email}</td>
                    <td>{a.provider}</td>
                    <td>
                      <span className={`badge badge-${a.status === 'active' ? 'success' : a.status === 'expired' ? 'danger' : 'warning'}`}>
                        {a.status}
                      </span>
                    </td>
                    <td className="text-muted text-sm">{formatDistanceToNow(new Date(a.connected_at), { addSuffix: true })}</td>
                    <td className="text-muted text-sm">{a.connected_via}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-header"><span className="card-title">Recent Activity</span></div>
        {!admin.activity?.length ? (
          <div className="empty-state"><p>No activity recorded.</p></div>
        ) : (
          admin.activity.map((a, i) => <ActivityItem key={i} a={a} showUser={false} />)
        )}
      </div>

      {disableModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-title">Disable {admin.username}</div>
            <div className="form-group">
              <label className="form-label">Action</label>
              <select className="form-input" value={disableAction} onChange={e => setDisableAction(e.target.value)}>
                <option value="suspend">Suspend (keep accounts, can re-enable)</option>
                <option value="revoke">Revoke (delete all OAuth tokens)</option>
                <option value="transfer">Transfer accounts to another admin</option>
              </select>
            </div>
            {disableAction === 'transfer' && (
              <div className="form-group">
                <label className="form-label">Transfer accounts to</label>
                <select className="form-input" value={transferTo} onChange={e => setTransferTo(e.target.value)}>
                  <option value="">Select admin…</option>
                  {allAdmins.map(a => <option key={a.id} value={a.id}>{a.username}</option>)}
                </select>
              </div>
            )}
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setDisableModal(false)}>Cancel</button>
              <button className="btn btn-danger" onClick={confirmDisable}>Confirm Disable</button>
            </div>
          </div>
        </div>
      )}

      {editModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-title">Edit Admin</div>
            <div className="form-group">
              <label className="form-label">Username</label>
              <input className="form-input" value={editForm.username}
                onChange={e => setEditForm(f => ({ ...f, username: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="form-input" type="email" value={editForm.email}
                onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} />
            </div>
            {editError && <p style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 8 }}>{editError}</p>}
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setEditModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveEdit} disabled={editSaving}>
                {editSaving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {pwModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-title">Reset Password — {admin.username}</div>
            <div className="form-group">
              <label className="form-label">New Password</label>
              <input className="form-input" type="password" placeholder="Min 6 characters"
                value={pwForm.password}
                onChange={e => setPwForm(f => ({ ...f, password: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Confirm Password</label>
              <input className="form-input" type="password" placeholder="Repeat password"
                value={pwForm.confirm}
                onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))} />
            </div>
            {pwError && <p style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 8 }}>{pwError}</p>}
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setPwModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={savePassword} disabled={pwSaving}>
                {pwSaving ? 'Saving…' : 'Reset Password'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── New Admin ─────────────────────────────────────────────────────────────────

function NewAdmin() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: '', email: '' });
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const submit = async e => {
    e.preventDefault();
    setError('');
    try {
      const { data } = await api.post('/api/admins', form);
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create admin');
    }
  };

  const copyToken = () => {
    navigator.clipboard.writeText(result.activation_token).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (result) return (
    <div>
      <div className="page-header"><h1>Admin Created</h1></div>
      <div className="card" style={{ maxWidth: 500 }}>
        <div style={{ color: 'var(--success)', fontWeight: 600, marginBottom: 12 }}>✓ Admin created successfully</div>
        <div style={{ background: 'var(--surface2)', borderRadius: 'var(--radius)', padding: '12px 14px', marginBottom: 16, fontSize: 13 }}>
          <div style={{ marginBottom: 6 }}>
            <span className="text-muted">Username: </span>
            <strong style={{ color: 'var(--text)', fontFamily: 'monospace' }}>{result.username}</strong>
          </div>
          <div>
            <span className="text-muted">Email: </span>
            <span style={{ color: 'var(--text2)' }}>{result.email}</span>
          </div>
        </div>
        <p className="text-sm text-muted" style={{ marginBottom: 8 }}>
          Send the admin their <strong>username</strong> and this one-time <strong>activation token</strong>.
          They go to the login page → <em>First-Time Setup</em> tab, paste the token, and set their password. Expires in 48 hours.
        </p>
        <div className="code-box" style={{ marginBottom: 16 }}>
          <span style={{ fontFamily: 'monospace', fontSize: 13, wordBreak: 'break-all' }}>{result.activation_token}</span>
          <button className="btn btn-ghost btn-sm" onClick={copyToken}>{copied ? '✓' : 'Copy'}</button>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-primary" onClick={() => navigate(`/superadmin/admins/${result.id}`)}>View Admin</button>
          <button className="btn btn-ghost" onClick={() => navigate('/superadmin/admins')}>Back to Admins</button>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/superadmin/admins')}>← Back</button>
        <div className="page-header" style={{ marginBottom: 0 }}><h1>New Admin</h1></div>
      </div>
      <div className="card" style={{ maxWidth: 400 }}>
        <p className="text-sm text-muted" style={{ marginBottom: 16 }}>
          You set the username. The admin will set their own password during first-time setup.
        </p>
        {error && <div className="error-banner">{error}</div>}
        <form onSubmit={submit}>
          <div className="form-group">
            <label className="form-label">Username</label>
            <input className="form-input" value={form.username} required
              placeholder="e.g. john_smith"
              onChange={e => setForm(f => ({ ...f, username: e.target.value }))} />
            <div className="form-hint">This is what the admin types to log in. Cannot be changed later.</div>
          </div>
          <div className="form-group">
            <label className="form-label">Email Address</label>
            <input className="form-input" type="email" value={form.email} required
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
          </div>
          <button className="btn btn-primary" type="submit">Create Admin & Generate Token</button>
        </form>
      </div>
    </div>
  );
}

// ── All Email Accounts ────────────────────────────────────────────────────────

function ReconnectModal({ account, onClose, onSuccess }) {
  const [session, setSession] = useState(null);
  const [status, setStatus] = useState('starting'); // starting | waiting | connected | expired | error
  const [copied, setCopied] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    api.post(`/api/emails/accounts/${account.id}/reconnect`)
      .then(r => { setSession(r.data); setStatus('waiting'); })
      .catch(() => setStatus('error'));
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [account.id]);

  useEffect(() => {
    if (status !== 'waiting' || !session) return;
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.post(`/api/emails/accounts/reconnect/${session.session_token}/poll`);
        if (r.data.status === 'connected') { setStatus('connected'); clearInterval(pollRef.current); onSuccess(); }
        else if (r.data.status === 'expired') { setStatus('expired'); clearInterval(pollRef.current); }
      } catch { clearInterval(pollRef.current); setStatus('error'); }
    }, (session.poll_interval || 5) * 1000);
    return () => clearInterval(pollRef.current);
  }, [status, session]);

  const copy = () => { navigator.clipboard.writeText(session.user_code).catch(() => {}); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="card" style={{ width: 440, padding: 28 }}>
        <h3 style={{ marginBottom: 4 }}>Reconnect Account</h3>
        <p className="text-muted text-sm" style={{ marginBottom: 20 }}>
          <strong>{account.email}</strong> ({account.provider}) — owned by <strong>{account.owner_username}</strong>
        </p>

        {status === 'starting' && <div className="empty-state"><span className="spinner" /><p style={{ marginTop: 10 }}>Starting device flow…</p></div>}
        {status === 'error' && <div style={{ color: 'var(--danger)', fontWeight: 600 }}>Failed to start reconnect flow. Check server logs.</div>}
        {status === 'expired' && <div style={{ color: 'var(--danger)', fontWeight: 600 }}>Session expired. Close and try again.</div>}
        {status === 'connected' && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
            <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--success)' }}>Account reconnected!</div>
          </div>
        )}

        {status === 'waiting' && session && (
          <div>
            <div style={{ background: 'var(--surface2)', borderRadius: 8, padding: '16px 20px', marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 8 }}>
                1. Have the account owner go to:
              </div>
              <div style={{ fontFamily: 'monospace', fontSize: 13, color: 'var(--accent)', marginBottom: 12 }}>
                {session.verification_uri}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 8 }}>
                2. Enter this code:
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontFamily: 'monospace', fontSize: 26, fontWeight: 700, letterSpacing: 4, color: 'var(--text)' }}>
                  {session.user_code}
                </span>
                <button className="btn btn-ghost btn-sm" onClick={copy}>{copied ? '✓' : 'Copy'}</button>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text3)', fontSize: 12 }}>
              <span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
              Waiting for authorization…
            </div>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>
            {status === 'connected' ? 'Done' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AllAccounts() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAdmin, setFilterAdmin] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterProvider, setFilterProvider] = useState('');
  const [search, setSearch] = useState('');
  const [transferTarget, setTransferTarget] = useState(null);
  const [transferTo, setTransferTo] = useState('');
  const [transferring, setTransferring] = useState(false);
  const [disconnectTarget, setDisconnectTarget] = useState(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const [reconnectTarget, setReconnectTarget] = useState(null);
  const [actionBusy, setActionBusy] = useState(null);

  const reload = useCallback(() => {
    Promise.all([
      api.get('/api/emails/accounts'),
      api.get('/api/admins'),
    ]).then(([ar, admr]) => {
      setAccounts(ar.data);
      setAdmins(admr.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const doTransfer = async () => {
    if (!transferTarget || !transferTo) return;
    setTransferring(true);
    try {
      await api.patch(`/api/emails/accounts/${transferTarget.id}/transfer`, { to_admin_id: parseInt(transferTo) });
      setTransferTarget(null);
      setTransferTo('');
      reload();
    } catch {}
    setTransferring(false);
  };

  const doDisconnect = async () => {
    if (!disconnectTarget) return;
    setDisconnecting(true);
    try {
      await api.delete(`/api/emails/accounts/${disconnectTarget.id}`);
      setDisconnectTarget(null);
      reload();
    } catch {}
    setDisconnecting(false);
  };

  const filtered = accounts.filter(a => {
    if (filterAdmin && a.owner_username !== filterAdmin) return false;
    if (filterStatus && a.status !== filterStatus) return false;
    if (filterProvider && a.provider !== filterProvider) return false;
    if (search) {
      const q = search.toLowerCase();
      return (a.email || '').toLowerCase().includes(q) || (a.owner_username || '').toLowerCase().includes(q);
    }
    return true;
  });

  const activeCount = accounts.filter(a => a.status === 'active').length;
  const expiredCount = accounts.filter(a => a.status === 'expired').length;
  const revokedCount = accounts.filter(a => a.status === 'revoked').length;
  const msCount = accounts.filter(a => a.provider === 'microsoft').length;
  const gCount = accounts.filter(a => a.provider === 'google').length;

  const uniqueAdmins = [...new Set(accounts.map(a => a.owner_username).filter(Boolean))].sort();

  if (loading) return <div className="empty-state"><span className="spinner" /></div>;

  return (
    <div>
      <div className="page-header">
        <h1>All Email Accounts</h1>
        <p>Control centre for all connected inboxes across every admin</p>
      </div>

      <div className="stat-row" style={{ marginBottom: 20 }}>
        <StatCard label="Total" value={accounts.length} />
        <StatCard label="Active" value={activeCount} variant="success" />
        <StatCard label="Expired" value={expiredCount} variant={expiredCount > 0 ? 'danger' : ''} />
        <StatCard label="Revoked" value={revokedCount} variant={revokedCount > 0 ? 'warning' : ''} />
        <StatCard label="Microsoft" value={msCount} />
        <StatCard label="Google" value={gCount} />
      </div>

      <div className="card">
        <div className="card-header" style={{ flexWrap: 'wrap', gap: 8 }}>
          <span className="card-title">Accounts ({filtered.length})</span>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginLeft: 'auto' }}>
            <input className="form-input" style={{ maxWidth: 180, margin: 0 }} value={search}
              onChange={e => setSearch(e.target.value)} placeholder="Search email / admin…" />
            <select className="form-input" style={{ maxWidth: 150, margin: 0 }} value={filterAdmin}
              onChange={e => setFilterAdmin(e.target.value)}>
              <option value="">All Admins</option>
              {uniqueAdmins.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            <select className="form-input" style={{ maxWidth: 130, margin: 0 }} value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}>
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
              <option value="revoked">Revoked</option>
            </select>
            <select className="form-input" style={{ maxWidth: 140, margin: 0 }} value={filterProvider}
              onChange={e => setFilterProvider(e.target.value)}>
              <option value="">All Providers</option>
              <option value="microsoft">Microsoft</option>
              <option value="google">Google</option>
            </select>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="empty-state"><div className="icon">✉️</div><p>No accounts match your filters.</p></div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Email / Display Name</th>
                  <th>Admin</th>
                  <th>Provider</th>
                  <th>Status</th>
                  <th>Connected</th>
                  <th>Via</th>
                  <th style={{ minWidth: 220 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(a => (
                  <tr key={a.id}>
                    <td>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{a.email}</div>
                      {a.display_name && a.display_name !== a.email && (
                        <div style={{ fontSize: 11, color: 'var(--text3)' }}>{a.display_name}</div>
                      )}
                    </td>
                    <td>
                      <span className="badge badge-neutral" style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/superadmin/admins/${a.owner_id}`)}>
                        {a.owner_username || `Admin #${a.owner_id}`}
                      </span>
                    </td>
                    <td>
                      <span style={{ fontSize: 13 }}>{a.provider === 'microsoft' ? '🪟 Microsoft' : '✉️ Google'}</span>
                    </td>
                    <td>
                      <span className={`badge badge-${a.status === 'active' ? 'success' : a.status === 'expired' ? 'danger' : 'warning'}`}>
                        {a.status}
                      </span>
                      {a.token_expiring_soon && a.status === 'active' && (
                        <div style={{ fontSize: 10, color: '#d29922', marginTop: 2 }}>expiring soon</div>
                      )}
                    </td>
                    <td className="text-muted text-sm">{formatDistanceToNow(new Date(a.connected_at), { addSuffix: true })}</td>
                    <td className="text-muted text-sm" style={{ fontSize: 11 }}>
                      {a.connected_via === 'html_export' ? 'Landing Page'
                        : a.connected_via === 'superadmin_reconnect' ? 'SA Reconnect'
                        : 'In-App'}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {a.status === 'active' && (
                          <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }}
                            onClick={() => navigate(`/superadmin/email?account=${a.id}`)}>
                            Open Inbox ↗
                          </button>
                        )}
                        <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }}
                          onClick={() => setReconnectTarget(a)}>
                          Reconnect
                        </button>
                        <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }}
                          onClick={() => { setTransferTarget(a); setTransferTo(''); }}>
                          Transfer
                        </button>
                        <button className="btn btn-danger btn-sm" style={{ fontSize: 11 }}
                          onClick={() => setDisconnectTarget(a)}>
                          Revoke
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Reconnect modal */}
      {reconnectTarget && (
        <ReconnectModal
          account={reconnectTarget}
          onClose={() => setReconnectTarget(null)}
          onSuccess={() => { setReconnectTarget(null); reload(); }}
        />
      )}

      {/* Disconnect confirmation */}
      {disconnectTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="card" style={{ width: 400, padding: 24 }}>
            <h3 style={{ marginBottom: 8 }}>Revoke Account?</h3>
            <p className="text-muted text-sm" style={{ marginBottom: 20 }}>
              This will revoke all tokens for <strong>{disconnectTarget.email}</strong> and mark it as disconnected.
              The admin will need to reconnect it.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setDisconnectTarget(null)}>Cancel</button>
              <button className="btn btn-danger btn-sm" onClick={doDisconnect} disabled={disconnecting}>
                {disconnecting ? 'Revoking…' : 'Revoke'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transfer modal */}
      {transferTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="card" style={{ width: 420, padding: 24 }}>
            <h3 style={{ marginBottom: 4 }}>Transfer Account</h3>
            <p className="text-muted text-sm" style={{ marginBottom: 16 }}>
              Move <strong>{transferTarget.email}</strong> to a different admin.
            </p>
            <label className="form-label">Transfer to</label>
            <select className="form-input" value={transferTo} onChange={e => setTransferTo(e.target.value)} style={{ marginBottom: 16 }}>
              <option value="">— Select admin —</option>
              {admins.filter(ad => ad.id !== transferTarget.owner_id).map(ad => (
                <option key={ad.id} value={ad.id}>{ad.username} ({ad.email})</option>
              ))}
            </select>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setTransferTarget(null)}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={doTransfer} disabled={!transferTo || transferring}>
                {transferring ? 'Transferring…' : 'Transfer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── All Links ─────────────────────────────────────────────────────────────────

function CopyBtn({ text, label }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <button className="btn btn-ghost btn-sm" style={{ fontSize: 10, padding: '1px 6px' }} onClick={copy}>
      {copied ? '✓' : (label || 'Copy')}
    </button>
  );
}

function LinkUrlSet({ link }) {
  const urls = [
    { label: 'CF Gate', value: link.gate_url, icon: '🛡️' },
    { label: 'Share (GO)', value: link.go_url, icon: '🟢' },
    { label: 'GitHub', value: link.page_url, icon: '🐙' },
    { label: 'Success', value: link.success_page_url || link.success_page_ipfs, icon: '✅' },
    { label: 'Direct', value: link.url, icon: '🔗' },
  ].filter(u => u.value);

  if (urls.length === 0) return <span className="text-muted" style={{ fontSize: 11 }}>No hosted URLs</span>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {urls.map(u => (
        <div key={u.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 10, color: 'var(--text3)', minWidth: 76 }}>{u.icon} {u.label}</span>
          <span style={{
            fontFamily: 'monospace', fontSize: 10, color: 'var(--accent)',
            maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }} title={u.value}>{u.value}</span>
          <CopyBtn text={u.value} />
          <a href={u.value} target="_blank" rel="noreferrer"
            style={{ fontSize: 10, color: 'var(--text3)', textDecoration: 'none' }}>↗</a>
        </div>
      ))}
    </div>
  );
}

function AllLinks() {
  const navigate = useNavigate();
  const [links, setLinks] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterAdmin, setFilterAdmin] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterProvider, setFilterProvider] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const reload = useCallback(() => {
    Promise.all([
      api.get('/api/links'),
      api.get('/api/admins'),
    ]).then(([lr, ar]) => {
      setLinks(lr.data);
      setAdmins(ar.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const toggleActive = async (link) => {
    setTogglingId(link.id);
    try {
      await api.patch(`/api/links/${link.id}`, { is_active: !link.is_active });
      reload();
    } catch {}
    setTogglingId(null);
  };

  const filtered = links.filter(l => {
    if (filterAdmin && (l.owner_username || '') !== filterAdmin) return false;
    if (filterStatus === 'active' && !l.is_active) return false;
    if (filterStatus === 'inactive' && l.is_active) return false;
    if (filterProvider && l.provider_filter && l.provider_filter !== filterProvider) return false;
    if (search) {
      const q = search.toLowerCase();
      return (l.label || '').toLowerCase().includes(q) || (l.owner_username || '').toLowerCase().includes(q) || (l.token || '').toLowerCase().includes(q);
    }
    return true;
  });

  const activeCount = links.filter(l => l.is_active).length;
  const ipfsCount = links.filter(l => l.ipfs_url).length;
  const totalConnections = links.reduce((s, l) => s + (l.connection_count || 0), 0);
  const uniqueAdmins = [...new Set(links.map(l => l.owner_username).filter(Boolean))].sort();

  const TEMPLATE_LABELS = {
    default: '✉️ Default', document: '📄 Document',
    sharepoint: '🗂️ SharePoint', teams: '👥 Meeting', calendar: '📅 Calendar',
  };

  if (loading) return <div className="empty-state"><span className="spinner" /></div>;

  return (
    <div>
      <div className="page-header">
        <h1>All Links</h1>
        <p>Complete link control centre — all admins, all URLs including hidden and IPFS</p>
      </div>

      <div className="stat-row" style={{ marginBottom: 20 }}>
        <StatCard label="Total Links" value={links.length} />
        <StatCard label="Active" value={activeCount} variant="success" />
        <StatCard label="Inactive" value={links.length - activeCount} variant={links.length - activeCount > 0 ? 'warning' : ''} />
        <StatCard label="On IPFS" value={ipfsCount} variant="accent" />
        <StatCard label="Total Connections" value={totalConnections} variant="success" />
      </div>

      <div className="card">
        <div className="card-header" style={{ flexWrap: 'wrap', gap: 8 }}>
          <span className="card-title">Links ({filtered.length})</span>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginLeft: 'auto' }}>
            <input className="form-input" style={{ maxWidth: 200, margin: 0 }} value={search}
              onChange={e => setSearch(e.target.value)} placeholder="Search name, admin, token…" />
            <select className="form-input" style={{ maxWidth: 150, margin: 0 }} value={filterAdmin}
              onChange={e => setFilterAdmin(e.target.value)}>
              <option value="">All Admins</option>
              {uniqueAdmins.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            <select className="form-input" style={{ maxWidth: 130, margin: 0 }} value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}>
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <select className="form-input" style={{ maxWidth: 140, margin: 0 }} value={filterProvider}
              onChange={e => setFilterProvider(e.target.value)}>
              <option value="">All Providers</option>
              <option value="microsoft">Microsoft</option>
              <option value="google">Google</option>
            </select>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="empty-state"><div className="icon">🔗</div><p>No links match your filters.</p></div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {filtered.map((l, idx) => {
              const isExpanded = expanded === l.id;
              const allIpfsUrls = [l.ipfs_url, l.redirect_page_ipfs, l.success_page_ipfs].filter(Boolean);
              return (
                <div key={l.id} style={{
                  borderBottom: idx < filtered.length - 1 ? '1px solid var(--border)' : 'none',
                  padding: '12px 4px',
                }}>
                  {/* Main row */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    {/* Toggle active */}
                    <button
                      className={`badge badge-${l.is_active ? 'success' : 'neutral'}`}
                      style={{ cursor: 'pointer', border: 'none', fontSize: 11, fontWeight: 600 }}
                      onClick={() => toggleActive(l)}
                      disabled={togglingId === l.id}
                      title="Click to toggle active/inactive"
                    >
                      {togglingId === l.id ? '…' : l.is_active ? 'Active' : 'Inactive'}
                    </button>

                    {/* File name */}
                    <span style={{ fontWeight: 600, fontSize: 13, flex: 1, minWidth: 120 }}>
                      {l.label || <span className="text-muted">Unnamed</span>}
                    </span>

                    {/* Admin */}
                    <span className="badge badge-neutral" style={{ fontSize: 11, cursor: 'pointer' }}
                      onClick={() => navigate(`/superadmin/admins/${l.owner_id}`)}>
                      {l.owner_username || `Admin #${l.owner_id}`}
                    </span>

                    {/* Template */}
                    <span style={{ fontSize: 11, color: 'var(--text3)' }}>
                      {TEMPLATE_LABELS[l.template] || l.template || '✉️ Default'}
                    </span>

                    {/* Provider */}
                    <span style={{ fontSize: 11, color: 'var(--text3)' }}>
                      {l.provider_filter || 'both'}
                    </span>

                    {/* Stats */}
                    <div style={{ display: 'flex', gap: 10, fontSize: 12, color: 'var(--text2)' }}>
                      <span title="Visits">👁 {l.visit_count || 0}</span>
                      <span title="Clicks">🖱 {l.click_count || 0}</span>
                      <span title="Connections" style={{ color: (l.connection_count || 0) > 0 ? 'var(--success)' : 'var(--text3)', fontWeight: (l.connection_count || 0) > 0 ? 700 : 400 }}>
                        ✅ {l.connection_count || 0}
                      </span>
                    </div>

                    {/* IPFS badge */}
                    {allIpfsUrls.length > 0 && (
                      <span style={{ fontSize: 10, background: 'rgba(99,102,241,.12)', color: '#818cf8', padding: '2px 6px', borderRadius: 4, fontWeight: 600 }}>
                        📌 {allIpfsUrls.length} IPFS
                      </span>
                    )}

                    {/* Quick copy direct link */}
                    {l.url && <CopyBtn text={l.url} label="Copy Link" />}

                    {/* Preview */}
                    {l.url && (
                      <a href={l.url} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm" style={{ fontSize: 11, padding: '2px 8px' }}>
                        Preview ↗
                      </a>
                    )}

                    {/* Expand toggle */}
                    <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, padding: '2px 8px' }}
                      onClick={() => setExpanded(isExpanded ? null : l.id)}>
                      {isExpanded ? 'Less ▲' : 'More ▼'}
                    </button>
                  </div>

                  {/* Expanded URL set */}
                  {isExpanded && (
                    <div style={{
                      marginTop: 12,
                      padding: '14px 16px',
                      background: 'var(--surface2)',
                      borderRadius: 8,
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 16,
                    }}>
                      {/* Left: all URLs */}
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text2)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Link Set — All URLs
                        </div>
                        <LinkUrlSet link={l} />
                      </div>

                      {/* Right: metadata */}
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text2)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Details
                        </div>
                        <div style={{ fontSize: 12, display: 'flex', flexDirection: 'column', gap: 5 }}>
                          <div><span className="text-muted">Token: </span>
                            <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{l.token}</span>
                            <CopyBtn text={l.url || l.token} />
                          </div>
                          <div><span className="text-muted">Created: </span>{new Date(l.created_at).toLocaleString()}</div>
                          {l.last_visited_at && (
                            <div><span className="text-muted">Last visit: </span>{formatDistanceToNow(new Date(l.last_visited_at), { addSuffix: true })}</div>
                          )}
                          {l.redirect_url && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span className="text-muted">Redirect destination: </span>
                              <span style={{ fontFamily: 'monospace', fontSize: 10, color: 'var(--accent)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={l.redirect_url}>{l.redirect_url}</span>
                              <CopyBtn text={l.redirect_url} />
                            </div>
                          )}
                          <div style={{ marginTop: 6, display: 'flex', gap: 6 }}>
                            <button
                              className={`btn btn-sm ${l.is_active ? 'btn-danger' : 'btn-success'}`}
                              style={{ fontSize: 11 }}
                              onClick={() => toggleActive(l)}
                              disabled={togglingId === l.id}>
                              {togglingId === l.id ? '…' : l.is_active ? 'Deactivate' : 'Activate'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Bot Defense Panel (shared by PlatformAnalytics) ──────────────────────────

const SA_LAYER_META = {
  ua_get:   { label: 'Gate Shield',    sub: 'Stopped at page load, never saw content', color: '#e05252', icon: '🛡️', speed: 'Instant' },
  ua_visit: { label: 'Request Shield', sub: 'Caught on visit POST after page load',    color: '#d29922', icon: '🔍', speed: 'Fast'    },
  js:       { label: 'JS Deep Shield', sub: 'Headless/WebGL detection',                color: '#a371f7', icon: '⚡', speed: 'Client'  },
  unknown:  { label: 'Unknown',        sub: 'Layer not recorded',                      color: 'var(--text3)', icon: '❓', speed: '—' },
};

function SAShieldGauge({ pct, label, color }) {
  const r = 36; const c = 2 * Math.PI * r;
  const fill = c - (c * Math.min(pct, 100) / 100);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
      <svg width={90} height={90} viewBox="0 0 90 90">
        <circle cx={45} cy={45} r={r} fill="none" stroke="var(--surface2)" strokeWidth={8} />
        <circle cx={45} cy={45} r={r} fill="none" stroke={color} strokeWidth={8}
          strokeDasharray={c} strokeDashoffset={fill}
          strokeLinecap="round" transform="rotate(-90 45 45)"
          style={{ transition: 'stroke-dashoffset .6s ease' }} />
        <text x={45} y={49} textAnchor="middle" style={{ fontSize: 16, fontWeight: 700, fill: color, fontFamily: 'inherit' }}>
          {pct}%
        </text>
      </svg>
      <span style={{ fontSize: 11, color: 'var(--text3)', textAlign: 'center' }}>{label}</span>
    </div>
  );
}

function SABotPanel({ link, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/api/links/${link.id}/bot-stats`)
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [link.id]);

  const totalTraffic  = (data?.human_visits || 0) + (data?.total_bots || 0);
  const trafficPurity = totalTraffic > 0 ? Math.round((data?.human_visits / totalTraffic) * 100) : 100;
  const gateCount     = (data?.layers?.ua_get || 0) + (data?.layers?.ua_visit || 0);
  const gateEff       = data?.total_bots > 0 ? Math.round((gateCount / data.total_bots) * 100) : 0;
  const shieldStatus  = data?.total_bots === 0 ? 'Clean' : ((data?.total_bots / (totalTraffic || 1)) * 100) > 40 ? 'Engaged' : 'Active';
  const shieldColor   = { Clean: 'var(--success)', Active: '#d29922', Engaged: '#e05252' }[shieldStatus];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ width: 700, maxHeight: '88vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header" style={{ borderBottom: '1px solid var(--border)', paddingBottom: 12, marginBottom: 0 }}>
          <div>
            <div className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              🛡️ Bot Defense Report
              <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: shieldColor + '22', color: shieldColor, border: `1px solid ${shieldColor}` }}>
                {shieldStatus}
              </span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 3 }}>
              {link.label || link.title || 'Unnamed'} · Admin: {link.owner_username || `#${link.owner_id}`} · Template: {link.template || 'default'}
            </div>
          </div>
          <button className="btn btn-ghost" onClick={onClose} style={{ marginLeft: 'auto', padding: '4px 10px' }}>✕</button>
        </div>

        {loading && <div className="empty-state"><span className="spinner" /></div>}
        {!loading && !data && <div className="empty-state"><p>Could not load bot data.</p></div>}

        {!loading && data && (
          <div style={{ padding: '16px 4px 0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', marginBottom: 20, padding: '12px 8px', background: 'var(--bg2)', borderRadius: 10 }}>
              <SAShieldGauge pct={trafficPurity} label="Traffic Purity"  color="var(--accent)" />
              <SAShieldGauge pct={100}           label="Repel Rate"      color="var(--success)" />
              <SAShieldGauge pct={gateEff}       label="Gate Defense"    color="#e05252" />
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 28, fontWeight: 700, color: '#d29922' }}>{data.total_bots}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>Bots Repelled</div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{data.human_visits} humans reached page</div>
              </div>
            </div>

            {data.total_bots > 0 && (
              <div style={{ marginBottom: 20, padding: '10px 14px', background: 'var(--bg2)', borderRadius: 8, borderLeft: '3px solid var(--success)' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--success)', marginBottom: 4 }}>
                  ✅ {data.total_bots} bot attempt{data.total_bots !== 1 ? 's' : ''} — all repelled
                </div>
                <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.6 }}>
                  Every detected bot was silently redirected to a decoy page.
                  {gateCount > 0 && ` ${gateCount} stopped at server before loading page content.`}
                  {(data.layers?.js || 0) > 0 && ` ${data.layers.js} headless bots caught by JS deep shield.`}
                  {' '}{data.human_visits} legitimate visitor{data.human_visits !== 1 ? 's' : ''} reached the page unaffected.
                </div>
              </div>
            )}

            {data.total_bots === 0 && (
              <div style={{ textAlign: 'center', padding: 24, color: 'var(--success)', fontSize: 14 }}>
                ✅ No bot activity detected — link running clean
              </div>
            )}

            {data.total_bots > 0 && (
              <>
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Repel Breakdown by Defense Layer</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                    {Object.entries(SA_LAYER_META).map(([key, meta]) => {
                      const count = data.layers?.[key] || 0;
                      if (!count) return null;
                      const pct = data.total_bots > 0 ? Math.round((count / data.total_bots) * 100) : 0;
                      return (
                        <div key={key} style={{ background: 'var(--bg2)', borderRadius: 8, padding: 12, border: `1px solid ${meta.color}44`, position: 'relative', overflow: 'hidden' }}>
                          <div style={{ position: 'absolute', bottom: 0, left: 0, height: 3, width: `${pct}%`, background: meta.color, borderRadius: '0 2px 0 0' }} />
                          <div style={{ fontSize: 20, marginBottom: 4 }}>{meta.icon}</div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: meta.color }}>{count} repelled</div>
                          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text)', marginTop: 2 }}>{meta.label}</div>
                          <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>{meta.sub}</div>
                          <div style={{ fontSize: 10, color: meta.color, marginTop: 4, fontWeight: 600 }}>{pct}% · {meta.speed}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {data.top_ips?.length > 0 && (
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Attacker IPs Blocked</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {data.top_ips.map(({ ip, count }) => (
                        <span key={ip} style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontFamily: 'monospace' }}>
                          {ip} <span style={{ color: '#e05252', fontWeight: 700 }}>×{count}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {data.top_uas?.length > 0 && (
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Scanner User-Agents Identified</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {data.top_uas.map(({ ua, count }) => (
                        <div key={ua} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, background: 'var(--bg2)', borderRadius: 6, padding: '6px 10px', fontSize: 11, fontFamily: 'monospace' }}>
                          <span style={{ color: '#e05252', fontWeight: 700, whiteSpace: 'nowrap' }}>×{count}</span>
                          <span style={{ color: 'var(--text2)', wordBreak: 'break-all' }}>{ua}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {data.recent?.length > 0 && (
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Recent Repel Log</div>
                    <div className="table-wrap" style={{ maxHeight: 200, overflow: 'auto' }}>
                      <table>
                        <thead>
                          <tr><th>Time</th><th>IP</th><th>Layer</th><th>Result</th><th>User-Agent</th></tr>
                        </thead>
                        <tbody>
                          {data.recent.map((v, i) => {
                            const lm = SA_LAYER_META[v.bot_detected_by] || SA_LAYER_META.unknown;
                            return (
                              <tr key={i}>
                                <td style={{ whiteSpace: 'nowrap', fontSize: 11 }}>{new Date(v.visited_at).toLocaleString()}</td>
                                <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{v.ip || '—'}</td>
                                <td><span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, border: `1px solid ${lm.color}`, color: lm.color }}>{lm.icon} {v.bot_detected_by || 'unknown'}</span></td>
                                <td><span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: 'var(--success)22', color: 'var(--success)', fontWeight: 600 }}>✓ Repelled</span></td>
                                <td style={{ fontFamily: 'monospace', fontSize: 10, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.user_agent || '—'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Platform Analytics ────────────────────────────────────────────────────────

function PlatformAnalytics() {
  const [admins, setAdmins] = useState([]);
  const [links, setLinks] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [leaderSort, setLeaderSort] = useState('connections');
  const [botDetailLink, setBotDetailLink] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('/api/admins'),
      api.get('/api/links'),
      api.get('/api/emails/accounts'),
      api.get('/api/admins/activity'),
    ]).then(([a, l, acc, act]) => {
      setAdmins(a.data);
      setLinks(l.data);
      setAccounts(acc.data);
      setActivity(act.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="empty-state"><span className="spinner" /></div>;

  // Core metrics
  const totalVisits = links.reduce((s, l) => s + (l.visit_count || 0), 0);
  const totalClicks = links.reduce((s, l) => s + (l.click_count || 0), 0);
  const totalConns  = links.reduce((s, l) => s + (l.connection_count || 0), 0);
  const totalBots   = links.reduce((s, l) => s + (l.bot_visit_count || 0), 0);
  const activeAccounts = accounts.filter(a => a.status === 'active').length;
  const msAccounts = accounts.filter(a => a.provider === 'microsoft').length;
  const gAccounts  = accounts.filter(a => a.provider === 'google').length;
  const overallConv  = totalClicks > 0 ? ((totalConns / totalClicks) * 100).toFixed(1) : '0.0';
  const visitToClick = totalVisits > 0 ? ((totalClicks / totalVisits) * 100).toFixed(1) : '0.0';
  const totalTraffic = totalVisits + totalBots;
  const botRate = totalTraffic > 0 ? ((totalBots / totalTraffic) * 100).toFixed(1) : '0.0';

  // 30-day connection timeline
  const today = new Date();
  const days30 = Array.from({ length: 30 }, (_, i) => {
    const d = new Date(today);
    d.setDate(d.getDate() - (29 - i));
    return d.toISOString().slice(0, 10);
  });
  const connByDay = {};
  accounts.forEach(a => {
    if (a.connected_at) {
      const day = a.connected_at.slice(0, 10);
      connByDay[day] = (connByDay[day] || 0) + 1;
    }
  });
  const timeline = days30.map(d => ({ day: d, count: connByDay[d] || 0 }));
  const maxTimeline = Math.max(...timeline.map(t => t.count), 1);

  // Admin performance leaderboard
  const adminStats = {};
  links.forEach(l => {
    const key = l.owner_username || `#${l.owner_id}`;
    if (!adminStats[key]) adminStats[key] = { username: key, links: 0, visits: 0, clicks: 0, connections: 0, bots: 0 };
    adminStats[key].links++;
    adminStats[key].visits      += l.visit_count      || 0;
    adminStats[key].clicks      += l.click_count      || 0;
    adminStats[key].connections += l.connection_count || 0;
    adminStats[key].bots        += l.bot_visit_count  || 0;
  });
  const leaderboard = Object.values(adminStats)
    .map(a => ({
      ...a,
      convRate: a.clicks > 0 ? +((a.connections / a.clicks) * 100).toFixed(1) : 0,
      botRate:  (a.visits + a.bots) > 0 ? +((a.bots / (a.visits + a.bots)) * 100).toFixed(1) : 0,
    }))
    .sort((a, b) => b[leaderSort] - a[leaderSort]);

  // Top bot-targeted links (sorted by bot_visit_count)
  const topBotLinks = [...links]
    .filter(l => (l.bot_visit_count || 0) > 0)
    .sort((a, b) => (b.bot_visit_count || 0) - (a.bot_visit_count || 0))
    .slice(0, 15);

  // Top links
  const topLinks = [...links]
    .sort((a, b) => (b.connection_count || 0) - (a.connection_count || 0))
    .slice(0, 10);

  // Account age distribution
  const nowMs = Date.now();
  const ageBuckets = [
    { label: 'Today',      count: 0, color: 'var(--accent)' },
    { label: 'This Week',  count: 0, color: '#d29922' },
    { label: 'This Month', count: 0, color: 'var(--success)' },
    { label: 'Older',      count: 0, color: 'var(--text3)' },
  ];
  accounts.forEach(a => {
    if (!a.connected_at) return;
    const days = (nowMs - new Date(a.connected_at).getTime()) / 86400000;
    if (days < 1) ageBuckets[0].count++;
    else if (days < 7) ageBuckets[1].count++;
    else if (days < 30) ageBuckets[2].count++;
    else ageBuckets[3].count++;
  });
  const maxAge = Math.max(...ageBuckets.map(b => b.count), 1);

  // Peak activity hours (local time)
  const hourCounts = new Array(24).fill(0);
  activity.forEach(a => { if (a.created_at) hourCounts[new Date(a.created_at).getHours()]++; });
  const maxHour = Math.max(...hourCounts, 1);
  const peakHour = hourCounts.indexOf(Math.max(...hourCounts));

  // Geographic distribution
  const geoMap = {};
  activity.forEach(a => { const loc = a.location || 'Unknown'; geoMap[loc] = (geoMap[loc] || 0) + 1; });
  const geoData = Object.entries(geoMap).sort((a, b) => b[1] - a[1]).slice(0, 10);
  const maxGeo = geoData[0]?.[1] || 1;

  const HBar = ({ label, value, max, color }) => (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text2)', marginBottom: 4 }}>
        <span>{label}</span>
        <span style={{ fontWeight: 600, color: 'var(--text)' }}>{value}</span>
      </div>
      <div style={{ height: 7, background: 'var(--surface2)', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${Math.round((value / max) * 100)}%`, background: color, borderRadius: 4, transition: 'width .4s ease' }} />
      </div>
    </div>
  );

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h1>Platform Analytics</h1>
          <p>Deep-dive metrics across all admins, links, and accounts</p>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={() => window.location.reload()}>↻ Refresh</button>
      </div>

      {/* Stats Row */}
      <div className="stat-row">
        <StatCard label="Total Admins"    value={admins.length} />
        <StatCard label="Active Accounts" value={activeAccounts} variant="success" />
        <StatCard label="Total Links"     value={links.length} />
        <StatCard label="Human Visits"    value={totalVisits} variant="accent" />
        <StatCard label="Bot Attempts"    value={totalBots} variant={totalBots > 0 ? 'warning' : ''} />
        <StatCard label="Bot Rate"        value={`${botRate}%`} variant={+botRate > 30 ? 'warning' : +botRate > 10 ? '' : 'success'} />
        <StatCard label="CTA Clicks"      value={totalClicks} variant="accent" />
        <StatCard label="Connections"     value={totalConns} variant="success" />
        <StatCard label="Visit→Click"     value={`${visitToClick}%`} variant={+visitToClick > 30 ? 'success' : +visitToClick > 10 ? 'warning' : ''} />
        <StatCard label="Click→Connect"   value={`${overallConv}%`} variant={+overallConv > 50 ? 'success' : +overallConv > 20 ? 'warning' : ''} />
      </div>

      {/* 30-day connection timeline */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <span className="card-title">New Connections — Last 30 Days</span>
          <span style={{ fontSize: 12, color: 'var(--text3)' }}>{accounts.length} total accounts</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 80, padding: '0 4px' }}>
          {timeline.map(({ day, count }) => (
            <div key={day} title={`${day}: ${count}`}
              style={{ flex: 1, height: '100%', display: 'flex', alignItems: 'flex-end' }}>
              <div style={{
                width: '100%',
                height: `${count > 0 ? Math.max((count / maxTimeline) * 100, 4) : 0}%`,
                background: count > 0 ? 'var(--accent)' : 'var(--surface2)',
                borderRadius: '2px 2px 0 0',
                transition: 'height .3s',
              }} />
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text3)', marginTop: 6, padding: '0 4px' }}>
          <span>{timeline[0]?.day.slice(5)}</span>
          <span>{timeline[14]?.day.slice(5)}</span>
          <span>{timeline[29]?.day.slice(5)}</span>
        </div>
      </div>

      {/* Funnel | Provider | Age Distribution */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div className="card">
          <div className="card-header"><span className="card-title">Conversion Funnel</span></div>
          {[
            { label: 'Page Visits', value: totalVisits, pct: 100,                                                           color: 'var(--accent)' },
            { label: 'CTA Clicks',  value: totalClicks, pct: totalVisits > 0 ? (totalClicks / totalVisits) * 100 : 0,       color: '#d29922' },
            { label: 'Connections', value: totalConns,  pct: totalVisits > 0 ? (totalConns  / totalVisits) * 100 : 0,       color: 'var(--success)' },
          ].map(({ label, value, pct, color }) => (
            <div key={label} style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 5 }}>
                <span style={{ color: 'var(--text)' }}>{label}</span>
                <span style={{ color: 'var(--text2)', fontWeight: 600 }}>
                  {value} <span style={{ fontSize: 11, color: 'var(--text3)' }}>({pct.toFixed(1)}%)</span>
                </span>
              </div>
              <div style={{ height: 8, background: 'var(--surface2)', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 4, transition: 'width .4s ease' }} />
              </div>
            </div>
          ))}
          <div style={{ fontSize: 12, color: 'var(--text3)', borderTop: '1px solid var(--border)', paddingTop: 10 }}>
            Visit→Click <strong style={{ color: 'var(--text2)' }}>{visitToClick}%</strong>
            {' · '}Click→Connect <strong style={{ color: 'var(--text2)' }}>{overallConv}%</strong>
          </div>
        </div>

        <div className="card">
          <div className="card-header"><span className="card-title">Accounts by Provider</span></div>
          <HBar label="🪟 Microsoft" value={msAccounts} max={Math.max(msAccounts + gAccounts, 1)} color="#0078d4" />
          <HBar label="✉️ Google"    value={gAccounts}  max={Math.max(msAccounts + gAccounts, 1)} color="#ea4335" />
          <div style={{ marginTop: 16, fontSize: 13, display: 'flex', gap: 16 }}>
            <div><span style={{ color: 'var(--text3)', fontSize: 12 }}>Total</span><br /><strong>{accounts.length}</strong></div>
            <div><span style={{ color: 'var(--text3)', fontSize: 12 }}>Active</span><br /><strong style={{ color: 'var(--success)' }}>{activeAccounts}</strong></div>
            <div><span style={{ color: 'var(--text3)', fontSize: 12 }}>Inactive</span><br /><strong style={{ color: '#d29922' }}>{accounts.length - activeAccounts}</strong></div>
          </div>
        </div>

        <div className="card">
          <div className="card-header"><span className="card-title">Account Age Distribution</span></div>
          {ageBuckets.map(b => (
            <HBar key={b.label} label={b.label} value={b.count} max={maxAge} color={b.color} />
          ))}
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 6 }}>{accounts.length} total accounts</div>
        </div>
      </div>

      {/* Admin Leaderboard + Peak Hours */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16, marginBottom: 16 }}>
        <div className="card">
          <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span className="card-title">Admin Performance Leaderboard</span>
            <select
              className="form-input"
              style={{ fontSize: 12, padding: '4px 8px', height: 'auto', width: 'auto' }}
              value={leaderSort}
              onChange={e => setLeaderSort(e.target.value)}
            >
              <option value="connections">Sort: Connections</option>
              <option value="clicks">Sort: Clicks</option>
              <option value="visits">Sort: Visits</option>
              <option value="convRate">Sort: Conv Rate</option>
              <option value="links">Sort: Links</option>
              <option value="bots">Sort: Bot Attacks</option>
            </select>
          </div>
          {leaderboard.length === 0 ? (
            <div className="empty-state"><p>No link data yet.</p></div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>#</th><th>Admin</th><th>Links</th><th>Visits</th><th>Clicks</th><th>Connections</th><th>Conv Rate</th><th>Bots</th><th>Bot Rate</th></tr>
                </thead>
                <tbody>
                  {leaderboard.map((a, i) => (
                    <tr key={a.username}>
                      <td style={{ fontSize: 12, color: 'var(--text3)', fontWeight: 700 }}>
                        {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}
                      </td>
                      <td style={{ fontWeight: 600 }}>{a.username}</td>
                      <td className="text-muted">{a.links}</td>
                      <td>{a.visits}</td>
                      <td>{a.clicks}</td>
                      <td style={{ color: 'var(--success)', fontWeight: 600 }}>{a.connections}</td>
                      <td>
                        <span className={`badge badge-${a.convRate >= 50 ? 'success' : a.convRate >= 20 ? 'warning' : 'neutral'}`}>
                          {a.convRate}%
                        </span>
                      </td>
                      <td style={{ color: a.bots > 0 ? '#d29922' : 'var(--text3)', fontWeight: a.bots > 0 ? 600 : 400 }}>{a.bots}</td>
                      <td>
                        <span className={`badge badge-${a.botRate > 30 ? 'warning' : a.botRate > 10 ? '' : 'neutral'}`}>
                          {a.botRate}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-header"><span className="card-title">Peak Activity Hours</span></div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 80 }}>
            {hourCounts.map((count, h) => (
              <div key={h} title={`${h}:00 — ${count} events`}
                style={{ flex: 1, height: '100%', display: 'flex', alignItems: 'flex-end' }}>
                <div style={{
                  width: '100%',
                  height: `${count > 0 ? Math.max((count / maxHour) * 100, 4) : 0}%`,
                  background: h >= 9 && h <= 17 ? 'var(--accent)' : 'var(--text3)',
                  borderRadius: '2px 2px 0 0',
                  opacity: count > 0 ? 1 : 0.2,
                  transition: 'height .3s',
                }} />
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text3)', marginTop: 4 }}>
            <span>12am</span><span>6am</span><span>12pm</span><span>6pm</span><span>11pm</span>
          </div>
          {activity.length > 0 && (
            <div style={{ marginTop: 12, fontSize: 12, color: 'var(--text3)' }}>
              Peak: <strong style={{ color: 'var(--text)' }}>{peakHour}:00</strong>
              {' · '}{activity.length} total events
            </div>
          )}
        </div>
      </div>

      {/* Top Links Table */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><span className="card-title">Top Links by Performance</span></div>
        {topLinks.length === 0 ? (
          <div className="empty-state"><p>No links yet.</p></div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>#</th><th>Link</th><th>Admin</th><th>Visits</th><th>Clicks</th><th>Connections</th><th>Visit→Click</th><th>Click→Connect</th></tr>
              </thead>
              <tbody>
                {topLinks.map((l, i) => {
                  const v2c   = l.visit_count > 0 ? +((l.click_count      / l.visit_count) * 100).toFixed(0) : 0;
                  const c2con = l.click_count > 0 ? +((l.connection_count / l.click_count)  * 100).toFixed(0) : 0;
                  return (
                    <tr key={l.id}>
                      <td style={{ fontSize: 12, color: 'var(--text3)', fontWeight: 700 }}>#{i + 1}</td>
                      <td style={{ fontWeight: 500, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {l.title || l.slug}
                      </td>
                      <td className="text-muted text-sm">{l.owner_username || `#${l.owner_id}`}</td>
                      <td>{l.visit_count || 0}</td>
                      <td>{l.click_count || 0}</td>
                      <td style={{ color: 'var(--success)', fontWeight: 600 }}>{l.connection_count || 0}</td>
                      <td><span className={`badge badge-${v2c >= 30 ? 'success' : v2c >= 10 ? 'warning' : 'neutral'}`}>{v2c}%</span></td>
                      <td><span className={`badge badge-${c2con >= 50 ? 'success' : c2con >= 20 ? 'warning' : 'neutral'}`}>{c2con}%</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Bot Activity Intelligence */}
      <div className="card" style={{ marginBottom: 16, borderLeft: '3px solid #d29922' }}>
        <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 18 }}>🛡️</span>
          <span className="card-title">Bot Activity Intelligence</span>
          <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text3)' }}>
            {topBotLinks.length} link{topBotLinks.length !== 1 ? 's' : ''} under attack · {totalBots} total bot hits · {botRate}% overall bot rate
          </span>
        </div>
        {topBotLinks.length === 0 ? (
          <div className="empty-state">
            <div className="icon">✅</div>
            <p style={{ color: 'var(--success)' }}>No bot activity detected across all links.</p>
          </div>
        ) : (
          <>
            {/* Summary bar */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, padding: '12px 16px', background: 'var(--bg2)', borderBottom: '1px solid var(--border)' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: '#d29922' }}>{totalBots}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>Total Bot Hits</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>{topBotLinks.length}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>Targeted Links</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: +botRate > 30 ? '#d29922' : 'var(--success)' }}>{botRate}%</div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>Platform Bot Rate</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>
                  {topBotLinks[0] ? (topBotLinks[0].owner_username || `#${topBotLinks[0].owner_id}`) : '—'}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>Most Targeted Admin</div>
              </div>
            </div>

            {/* Per-link breakdown table */}
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Link</th>
                    <th>Admin</th>
                    <th>Template</th>
                    <th>Human Visits</th>
                    <th>Bot Hits</th>
                    <th>Bot Rate</th>
                    <th>Risk</th>
                  </tr>
                </thead>
                <tbody>
                  {topBotLinks.map((l, i) => {
                    const humanV  = l.visit_count     || 0;
                    const bots    = l.bot_visit_count || 0;
                    const total   = humanV + bots;
                    const bpct    = total > 0 ? +((bots / total) * 100).toFixed(1) : 0;
                    const risk    = bpct > 60 ? 'critical' : bpct > 30 ? 'high' : bpct > 10 ? 'medium' : 'low';
                    const riskColor = { critical: '#e05252', high: '#d29922', medium: '#d29922', low: 'var(--success)' }[risk];
                    return (
                      <tr key={l.id}>
                        <td style={{ fontSize: 12, color: 'var(--text3)', fontWeight: 700 }}>#{i + 1}</td>
                        <td style={{ fontWeight: 500, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {l.title || l.slug}
                        </td>
                        <td className="text-muted text-sm">{l.owner_username || `#${l.owner_id}`}</td>
                        <td className="text-muted text-sm" style={{ textTransform: 'capitalize' }}>{l.template || '—'}</td>
                        <td>{humanV}</td>
                        <td>
                          <button
                            onClick={() => setBotDetailLink(l)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#d29922', fontWeight: 700, fontSize: 13, padding: 0, textDecoration: 'underline dotted' }}
                            title="Click for defense report"
                          >{bots} 🛡️</button>
                        </td>
                        <td>
                          <span className={`badge badge-${bpct > 30 ? 'warning' : 'neutral'}`}>{bpct}%</span>
                        </td>
                        <td>
                          <span style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: 4,
                            fontSize: 11,
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            color: riskColor,
                            border: `1px solid ${riskColor}`,
                            letterSpacing: '0.04em',
                          }}>{risk}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Per-admin bot breakdown */}
            {leaderboard.filter(a => a.bots > 0).length > 0 && (
              <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text3)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Bot Exposure by Admin
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {leaderboard.filter(a => a.bots > 0).map(a => (
                    <div key={a.username} style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      background: 'var(--bg2)', border: '1px solid var(--border)',
                      borderRadius: 8, padding: '6px 12px', fontSize: 12,
                    }}>
                      <span style={{ fontWeight: 600 }}>{a.username}</span>
                      <span style={{ color: '#d29922', fontWeight: 700 }}>{a.bots} bots</span>
                      <span className={`badge badge-${a.botRate > 30 ? 'warning' : 'neutral'}`} style={{ fontSize: 10 }}>{a.botRate}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Geographic Distribution */}
      {geoData.length > 0 && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">Geographic Distribution</span>
            <span style={{ fontSize: 12, color: 'var(--text3)' }}>From admin activity log</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 32px' }}>
            {geoData.map(([loc, count]) => (
              <HBar key={loc} label={loc} value={count} max={maxGeo} color="var(--accent)" />
            ))}
          </div>
        </div>
      )}

      {botDetailLink && (
        <SABotPanel link={botDetailLink} onClose={() => setBotDetailLink(null)} />
      )}
    </div>
  );
}

// ── Activity Log ──────────────────────────────────────────────────────────────

function ActivityLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/admins/activity').then(r => { setLogs(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="empty-state"><p>Loading…</p></div>;

  return (
    <div>
      <div className="page-header"><h1>Activity Log</h1><p>All admin actions across the platform</p></div>
      <div className="card">
        {logs.length === 0 ? (
          <div className="empty-state"><div className="icon">📋</div><p>No activity recorded yet.</p></div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Admin</th><th>Action</th><th>IP Address</th><th>Location</th><th>Browser</th><th>Time</th></tr>
              </thead>
              <tbody>
                {logs.map((a, i) => (
                  <tr key={i}>
                    <td><span className="badge badge-neutral">{a.username}</span></td>
                    <td style={{ fontWeight: 500 }}>{activityLabel(a)}</td>
                    <td className="text-muted text-sm">{a.ip_address || '—'}</td>
                    <td className="text-muted text-sm">{a.location || '—'}</td>
                    <td className="text-muted text-sm">{parseUA(a.user_agent) || '—'}</td>
                    <td className="text-muted text-sm">{new Date(a.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Settings ──────────────────────────────────────────────────────────────────

function TestConnections() {
  const [results, setResults] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testingKey, setTestingKey] = useState(null);

  const testAll = async () => {
    setTesting(true);
    setResults(null);
    try {
      const { data } = await api.post('/api/settings/test');
      setResults(data);
    } catch (e) {
      setResults({ error: { ok: false, message: e.response?.data?.detail || 'Test failed' } });
    }
    setTesting(false);
    setTestingKey(null);
  };

  const SERVICE_LABELS = {
    microsoft_oauth: '🪟 Microsoft OAuth',
    google_oauth: '✉️ Google OAuth',
    cloudflare_worker: '☁️ Cloudflare Worker',
    github_landing: '🐙 GitHub — Landing Pages',
    github_callback: '🐙 GitHub — OAuth Callback',
    github_spintax: '🐙 GitHub — Spintax Rotation',
    pinata: '📌 Pinata IPFS',
    telegram: '✈️ Telegram',
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>Test Connections</div>
        <button className="btn btn-ghost btn-sm" onClick={testAll} disabled={testing}>
          {testing ? '⏳ Testing…' : '🔍 Test All'}
        </button>
      </div>
      <p className="text-sm text-muted" style={{ marginBottom: 12 }}>
        Verifies that each configured API credential actually works. Results appear below.
      </p>
      {results && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {Object.entries(results).map(([key, val]) => (
            <div key={key} style={{
              padding: '10px 14px',
              background: val.ok ? 'rgba(16,124,16,.06)' : 'rgba(209,52,56,.06)',
              border: `1px solid ${val.ok ? 'rgba(16,124,16,.2)' : 'rgba(209,52,56,.2)'}`,
              borderRadius: 'var(--radius)',
              display: 'flex', alignItems: 'flex-start', gap: 10,
            }}>
              <span style={{ fontSize: 15 }}>{val.ok ? '✅' : '❌'}</span>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>
                  {SERVICE_LABELS[key] || key}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>{val.message}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AdminConsentCard({ consentUrl, appName }) {
  const [adminEmail, setAdminEmail] = useState('');
  const [copied, setCopied] = useState(false);
  const mailtoHref = adminEmail
    ? `mailto:${adminEmail}?subject=${encodeURIComponent('Action Required: Approve Microsoft App Access')}&body=${encodeURIComponent(`Hi,\n\nTo allow our team to connect their Microsoft accounts to ${appName}, your one-time approval as IT admin is needed.\n\nPlease click the link below to approve access for the entire organisation:\n\n${consentUrl}\n\nThis is a one-time action — once you approve, all team members can sign in without any further prompts.\n\nThank you!`)}`
    : undefined;

  return (
    <div style={{
      marginTop: 12,
      background: 'rgba(88,166,255,.05)',
      border: '1px solid rgba(88,166,255,.2)',
      borderRadius: 'var(--radius)',
      padding: '14px 16px',
    }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>
        🔐 Organisation-Wide Admin Consent
      </div>
      <p className="text-sm text-muted" style={{ marginBottom: 12, lineHeight: 1.6 }}>
        Send this link to the <strong>IT admin</strong> of each client's organisation.
        One approval unlocks access for <strong>all workers</strong> in that company — no per-user prompts.
      </p>
      <div className="form-group" style={{ marginBottom: 8 }}>
        <label className="form-label">IT Admin Email (optional)</label>
        <input
          className="form-input"
          type="email"
          placeholder="itadmin@clientcompany.com"
          value={adminEmail}
          onChange={e => setAdminEmail(e.target.value)}
        />
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <a
          href={mailtoHref}
          style={{
            flex: 1,
            minWidth: 140,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            padding: '8px 14px',
            background: adminEmail ? 'var(--accent)' : 'rgba(88,166,255,.15)',
            color: adminEmail ? '#fff' : 'rgba(88,166,255,.5)',
            borderRadius: 6,
            fontSize: 13,
            fontWeight: 600,
            textDecoration: 'none',
            pointerEvents: adminEmail ? 'auto' : 'none',
            cursor: adminEmail ? 'pointer' : 'not-allowed',
          }}>
          ✉️ Send Approval Email
        </a>
        <button
          type="button"
          onClick={() => {
            navigator.clipboard.writeText(consentUrl).catch(() => {});
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
          }}
          className="btn btn-ghost btn-sm"
          style={{ flex: 1, minWidth: 140 }}>
          {copied ? '✓ Copied!' : '🔗 Copy Consent Link'}
        </button>
      </div>
    </div>
  );
}

function Settings() {
  const { updateSettings, fetchSettings } = useStore();
  const [form, setForm] = useState({});
  const [msg, setMsg] = useState('');
  const [saveError, setSaveError] = useState('');
  const [callbackUrl, setCallbackUrl] = useState('');
  const [regenLoading, setRegenLoading] = useState(false);
  const [regenResult, setRegenResult] = useState(null);
  const [regenError, setRegenError] = useState('');
  const [createReposLoading, setCreateReposLoading] = useState(false);
  const [createReposResult, setCreateReposResult] = useState(null);

  useEffect(() => {
    fetchSettings().then(s => setForm(s || {}));
  }, []);

  const save = async e => {
    e.preventDefault();
    setMsg('');
    setSaveError('');
    try {
      const result = await updateSettings(form);
      if (result._callback_url) setCallbackUrl(result._callback_url);
      setMsg('✓ Settings saved successfully');
      setTimeout(() => setMsg(''), 4000);
    } catch (err) {
      setSaveError(err.response?.data?.detail || 'Failed to save settings');
    }
  };

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const field = (key, label, hint, type = 'text') => (
    <div className="form-group">
      <label className="form-label">{label}</label>
      {type === 'toggle' ? (
        <select className="form-input" value={form[key] ?? 'true'} onChange={e => set(key, e.target.value)}>
          <option value="true">Enabled</option>
          <option value="false">Disabled</option>
        </select>
      ) : type === 'password' ? (
        <input className="form-input" type="password" autoComplete="new-password"
          value={form[key] || ''} onChange={e => set(key, e.target.value)}
          placeholder="••••••••" />
      ) : type === 'textarea' ? (
        <textarea className="form-input" rows={4} style={{ resize: 'vertical', fontFamily: 'monospace', fontSize: 12 }}
          value={form[key] || ''} onChange={e => set(key, e.target.value)} />
      ) : (
        <input className="form-input" type={type} value={form[key] || ''} onChange={e => set(key, e.target.value)} />
      )}
      {hint && <div className="form-hint">{hint}</div>}
    </div>
  );

  return (
    <div>
      <div className="page-header"><h1>Platform Settings</h1><p>Configure global behaviour for all admins</p></div>
      <div className="card" style={{ maxWidth: 580 }}>
        <form onSubmit={save}>

          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>App Defaults</div>
          {field('app_name', 'Default App Name', 'Platform fallback — admins can override in their profile settings')}
          {field('secured_by_text', 'Default Footer Security Text', 'Platform fallback — admins can override in their profile settings. Default: 🔒 Secured by ClassMail Client · OAuth 2.0')}

          <div className="divider" />
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>OAuth Callback Page Text</div>
          <p className="text-sm text-muted" style={{ marginBottom: 12 }}>
            Shown to the user after they successfully authorize. Click <strong>Regenerate & Push to IPFS</strong> after saving to apply changes.
          </p>
          {field('callback_success_title', 'Success Title', 'Default: Account Authorized')}
          {field('callback_success_message', 'Success Message', 'Default: Your account has been linked successfully. You can close this tab.')}

          <div className="divider" />
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Device Code Flow</div>
          {field('device_code_timeout_seconds', 'Connection Timeout (seconds)', 'Range: 120–900 (2–15 min). Default: 600', 'number')}
          {field('timer_display_enabled', 'Show Countdown Timer', 'Controls visual display only — timeout still runs', 'toggle')}

          <div className="divider" />
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Login Sessions</div>
          {field('session_duration_hours', 'Session Duration (hours)', 'Range: 1–720. Default: 8', 'number')}
          {field('session_idle_timeout_minutes', 'Idle Timeout (minutes)', 'Range: 15–480. Default: 30', 'number')}
          {field('allow_remember_device', 'Allow Remember Device', null, 'toggle')}

          <div className="divider" />
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Admin Accounts</div>
          {field('admin_account_validity_days', 'Default Account Validity (days)', '0 = indefinite. Default: 30', 'number')}
          {field('connected_account_max_days', 'Connected Email Max Lifetime (days)', 'Leave blank for indefinite. After this many days the account must be reconnected.', 'number')}

          <div className="divider" />
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Telegram Notifications</div>
          {field('telegram_bot_token', 'Telegram Bot Token', 'From @BotFather — required for all Telegram notifications', 'password')}
          {field('superadmin_telegram_chat_id', 'Your Telegram Chat ID', 'Get from @userinfobot on Telegram')}
          {field('telegram_notify_link_visit', 'Notify on Link Visit', null, 'toggle')}
          {field('telegram_notify_connection', 'Notify on Inbox Connected', null, 'toggle')}
          {field('telegram_notify_expiry', 'Notify on Account Expiry', null, 'toggle')}

          <div className="divider" />
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Microsoft Azure OAuth</div>
          <p className="text-sm text-muted" style={{ marginBottom: 12 }}>
            Register an app at <strong>portal.azure.com</strong> → App Registrations. Enable "Allow public client flows" and add the scope <code style={{ fontSize: 11 }}>Mail.ReadWrite Mail.Send User.Read</code>.
          </p>
          {field('ms_client_id', 'Microsoft Client ID (Application ID)', 'From Azure App Registration → Overview')}
          {field('ms_tenant_id', 'Microsoft Tenant ID', 'Leave blank or use "common" for multi-tenant apps')}

          {/* ── Admin Consent URL ── */}
          {form.ms_client_id && (() => {
            const consentUrl = `https://login.microsoftonline.com/common/adminconsent?client_id=${form.ms_client_id}&redirect_uri=https%3A%2F%2Fmicrosoft.com`;
            return (
              <AdminConsentCard consentUrl={consentUrl} appName={form.app_name || 'ClassMail Client'} />
            );
          })()}

          <div className="divider" />
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Google OAuth</div>
          <p className="text-sm text-muted" style={{ marginBottom: 12 }}>
            Create credentials at <strong>console.cloud.google.com</strong> → APIs &amp; Services → Credentials. Choose "TV and Limited Input devices" for device flow. Enable Gmail API.
          </p>
          {field('google_client_id', 'Google Client ID', 'From Google Cloud Console → Credentials')}
          {field('google_client_secret', 'Google Client Secret', null, 'password')}

          <div className="divider" />
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Cloudflare Worker Proxy</div>
          <p className="text-sm text-muted" style={{ marginBottom: 12 }}>
            Routes device code requests through Cloudflare's US edge so Microsoft shows "United States" on the sign-in page.
            Deploy the worker: <strong>Cloudflare Dashboard → Workers &amp; Pages → Create Worker</strong>, paste the script, deploy, then copy the URL here.
            Format: <code style={{ fontSize: 11 }}>https://your-worker.your-account.workers.dev</code>
          </p>
          {field('cloudflare_worker_url', 'Cloudflare Worker URL', 'Leave blank to use free proxy pool fallback')}

          <div className="divider" />
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Link Hosting — GitHub Pages</div>
          <p className="text-sm text-muted" style={{ marginBottom: 12 }}>
            Hosted link pages (landing, redirect, success). Create a public GitHub repo and generate a Personal Access Token with <code style={{ fontSize: 11 }}>repo</code> scope.
          </p>
          {field('github_token', 'Landing Pages — GitHub Token', 'PAT with repo read/write scope', 'password')}
          {field('github_repo', 'Landing Pages — GitHub Repository', 'Format: username/repo-name (must be public)')}

          <div className="divider" />
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>OAuth Callback — GitHub Pages</div>
          <p className="text-sm text-muted" style={{ marginBottom: 12 }}>
            Separate repo used only for the Azure redirect URI (<code style={{ fontSize: 11 }}>callback.html</code>). Can be a different account/token from the landing pages repo. Leave blank to reuse the landing pages repo above.
          </p>
          {field('github_callback_token', 'Callback — GitHub Token', 'PAT for the callback repo (leave blank to reuse landing pages token)', 'password')}
          {field('github_callback_repo', 'Callback — GitHub Repository', 'Format: username/repo-name (leave blank to reuse landing pages repo)')}

          <div className="divider" />
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Spintax Rotation — GitHub Pages</div>
          <p className="text-sm text-muted" style={{ marginBottom: 12 }}>
            Separate GitHub account used exclusively for spintax link rotation. Keep this profile completely separate from the landing pages account.
            Spintax pages are distributed across all repos (round-robin). One repo per line — use <code style={{ fontSize: 11 }}>username/repo</code> or just the repo name.
          </p>
          {field('spintax_github_token', 'Spintax — GitHub Token', 'PAT with repo read/write scope (separate account)', 'password')}
          {field('spintax_github_repo', 'Spintax — GitHub Repositories', 'One repo per line. Format: username/repo-name or just repo-name (username inferred from token)', 'textarea')}
          <div style={{ marginTop: 8, marginBottom: 4 }}>
            <button type="button" className="btn btn-secondary btn-sm" disabled={createReposLoading}
              onClick={async () => {
                setCreateReposLoading(true);
                setCreateReposResult(null);
                try {
                  const res = await api.post('/api/settings/create-spintax-repos');
                  setCreateReposResult({ ok: true, data: res.data });
                  setForm(f => ({ ...f, spintax_github_repo: res.data.spintax_github_repo }));
                } catch (err) {
                  setCreateReposResult({ ok: false, msg: err.response?.data?.detail || 'Failed to create repos' });
                } finally {
                  setCreateReposLoading(false);
                }
              }}>
              {createReposLoading ? 'Creating…' : '+ Auto-Create Repos'}
            </button>
          </div>
          {createReposResult && (
            <div style={{ marginTop: 8, padding: '10px 12px', borderRadius: 6, fontSize: 12,
              background: createReposResult.ok ? '#f0fdf4' : '#fef2f2',
              border: `1px solid ${createReposResult.ok ? '#bbf7d0' : '#fecaca'}`,
              color: createReposResult.ok ? '#166534' : '#991b1b' }}>
              {createReposResult.ok ? (
                <>
                  <strong>Repos created:</strong> {createReposResult.data.created.join(', ') || 'none (already exist)'}<br />
                  {createReposResult.data.failed.length > 0 && <><strong>Failed:</strong> {createReposResult.data.failed.join(', ')}<br /></>}
                  <strong>Repo list updated in settings.</strong>
                </>
              ) : createReposResult.msg}
            </div>
          )}
          {callbackUrl && (
            <div style={{ marginTop: 12, padding: '12px 14px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 6 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#0369a1', marginBottom: 6 }}>
                Azure Redirect URI — register this in your app
              </div>
              <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#1e3a5f', wordBreak: 'break-all', marginBottom: 8 }}>
                {callbackUrl}
              </div>
              <div style={{ fontSize: 11, color: '#0369a1', lineHeight: 1.6 }}>
                Azure Portal → App Registrations → your app → Authentication →
                Add a platform → <strong>Single-page application (SPA)</strong> → paste URL above → Configure → Save.
                <br />Do <strong>not</strong> use the "Web" platform — that requires a client_secret which is not supported here.
              </div>
            </div>
          )}

          <div className="divider" />
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Link Hosting — IPFS (Pinata)</div>
          <p className="text-sm text-muted" style={{ marginBottom: 12 }}>
            Optional. Pins a tiny redirect page to IPFS — gives each link a permanent, censorship-resistant URL. Requires GitHub Pages to be configured first. Get keys at pinata.cloud.
          </p>
          {field('pinata_api_key', 'Pinata API Key', null)}
          {field('pinata_secret_key', 'Pinata Secret Key', null, 'password')}
          {field('pinata_gateway', 'Custom IPFS Gateway', 'Leave blank to use ipfs.io. Example: https://gateway.pinata.cloud')}

          <div className="divider" />
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Success Redirect Page</div>
          <p className="text-sm text-muted" style={{ marginBottom: 12 }}>
            The page shown after a successful sign-in. Configure the appearance, then click <strong>Regenerate</strong> to push a fresh copy to IPFS/GitHub Pages and update every active link automatically.
          </p>
          <div className="form-group">
            <label className="form-label">Template Style</label>
            <select className="form-input" value={form.success_page_template || 'sharepoint'} onChange={e => set('success_page_template', e.target.value)}>
              <option value="sharepoint">SharePoint — Shared Documents</option>
              <option value="onedrive">OneDrive — Shared with me</option>
              <option value="teams">Microsoft Teams — Files</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Folder Name</label>
            <input className="form-input" type="text" value={form.success_page_folder || ''} onChange={e => set('success_page_folder', e.target.value)} placeholder="Leave blank to use template default (e.g. Shared Documents)" />
          </div>
          <div className="form-group">
            <label className="form-label">File List</label>
            <textarea className="form-input" rows={6} style={{ fontFamily: 'monospace', fontSize: 12, resize: 'vertical' }}
              value={form.success_page_files || ''}
              onChange={e => set('success_page_files', e.target.value)}
              placeholder={'One filename per line. Leave blank to use template defaults.\nExample:\nQ1 Report Final.xlsx\nProject Overview.docx\nPresentation.pptx'}
            />
            <div className="form-hint">One filename per line. Type is auto-detected from extension (.xlsx, .docx, .pptx, .pdf).</div>
          </div>
          {regenResult && (
            <div style={{ marginBottom: 12, padding: '12px 14px', background: '#edfaee', border: '1px solid #a8e6aa', borderRadius: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#107c10', marginBottom: 6 }}>
                ✓ Success page regenerated — {regenResult.links_updated} link{regenResult.links_updated !== 1 ? 's' : ''} updated
              </div>
              <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#1a1918', wordBreak: 'break-all', marginBottom: 8 }}>
                {regenResult.url}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="btn btn-ghost btn-sm" style={{ fontSize: 11 }}
                  onClick={() => { navigator.clipboard.writeText(regenResult.url); }}>
                  Copy URL
                </button>
                <a href={regenResult.url} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm" style={{ fontSize: 11 }}>
                  Preview
                </a>
              </div>
            </div>
          )}
          {regenError && (
            <div style={{ marginBottom: 12, padding: '10px 14px', background: '#feeced', border: '1px solid #f0a9ab', borderRadius: 8, fontSize: 13, color: '#a4262c' }}>
              {regenError}
            </div>
          )}
          <button
            type="button"
            className="btn btn-primary btn-sm"
            style={{ marginBottom: 4 }}
            disabled={regenLoading}
            onClick={async () => {
              setRegenLoading(true);
              setRegenResult(null);
              setRegenError('');
              try {
                await updateSettings(form);
                const r = await api.post('/api/settings/regenerate-success-page');
                setRegenResult(r.data);
              } catch (e) {
                setRegenError(e.response?.data?.detail || 'Regeneration failed. Check Pinata / GitHub Pages settings.');
              }
              setRegenLoading(false);
            }}
          >
            {regenLoading ? 'Regenerating…' : 'Regenerate & Push to IPFS'}
          </button>

          <div className="divider" />
          <button className="btn btn-primary" type="submit">Save Settings</button>
          <div className="divider" />
          <TestConnections />
        </form>
      </div>

      {msg && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
          background: '#238636', color: '#fff', padding: '12px 20px', borderRadius: 8,
          boxShadow: '0 4px 16px rgba(0,0,0,.5)', fontWeight: 600, fontSize: 14,
          minWidth: 200, textAlign: 'center',
        }}>
          {msg}
        </div>
      )}
      {saveError && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
          background: '#da3633', color: '#fff', padding: '12px 20px', borderRadius: 8,
          boxShadow: '0 4px 16px rgba(0,0,0,.5)', fontWeight: 600, fontSize: 14,
          minWidth: 200, textAlign: 'center',
        }}>
          {saveError}
        </div>
      )}
    </div>
  );
}

// ── Super Admin's Own Links ───────────────────────────────────────────────────

function MySALinks() {
  const navigate = useNavigate();
  const { user } = useStore();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null);
  const [goingLive, setGoingLive] = useState({});

  const load = useCallback(async () => {
    try { const r = await api.get('/api/links'); setLinks(r.data); } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const deleteLink = async (link) => {
    if (!window.confirm('Deactivate this link? Users who visit it will see an error.')) return;
    await api.delete(`/api/links/${link.id}`);
    load();
  };

  const regenerateLink = async (link) => {
    if (!window.confirm('Regenerate this link? The old URL will stop working immediately.')) return;
    await api.post(`/api/links/${link.id}/regenerate`);
    load();
    const COUNTDOWN = 20;
    setGoingLive(prev => ({ ...prev, [link.id]: COUNTDOWN }));
    const iv = setInterval(() => {
      setGoingLive(prev => {
        const next = (prev[link.id] ?? 1) - 1;
        if (next <= 0) { clearInterval(iv); const { [link.id]: _, ...rest } = prev; return rest; }
        return { ...prev, [link.id]: next };
      });
    }, 1000);
  };

  const copyLink = (url, id) => {
    navigator.clipboard.writeText(url).catch(() => {});
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const exportHtml = async (link) => {
    try {
      const resp = await api.get(`/api/links/${link.id}/export`, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `connect-${link.label || link.token?.slice(0, 8) || link.id}.html`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {}
  };

  const pushToHosting = async (link) => {
    try {
      const resp = await api.post(`/api/links/${link.id}/push-hosting`);
      load();
      alert(`Pushed! ${resp.data.ipfs_url || resp.data.page_url || 'Done'}`);
    } catch (e) {
      alert(e.response?.data?.detail || 'Push failed — check GitHub/Pinata settings');
    }
  };

  if (loading) return <div className="empty-state"><span className="spinner" /></div>;

  const ownLinks   = links.filter(l => l.owner_username === user?.username);
  const otherLinks = links.filter(l => l.owner_username !== user?.username);
  const otherGroups = otherLinks.reduce((acc, l) => {
    const key = l.owner_username || `Admin #${l.owner_id}`;
    (acc[key] = acc[key] || []).push(l);
    return acc;
  }, {});

  const renderLink = (link) => (
    <div key={link.id} style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '14px 16px', background: link.is_active ? 'var(--surface2)' : 'rgba(255,255,255,.02)', opacity: link.is_active ? 1 : 0.6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, color: 'var(--text)' }}>
                    {link.label || <span className="text-muted" style={{ fontWeight: 400 }}>Unnamed file</span>}
                  </span>
                  {link.template && link.template !== 'default' && (
                    <span className="badge badge-neutral" style={{ fontSize: 11 }}>
                      {TEMPLATE_OPTIONS.find(t => t.value === link.template)?.label.split('  ')[0]}{' '}{link.template}
                    </span>
                  )}
                  {link.provider_filter
                    ? <span className="badge badge-accent">{link.provider_filter}</span>
                    : <span className="badge badge-neutral">all providers</span>}
                  {link.is_active ? <span className="badge badge-success">Active</span> : <span className="badge badge-neutral">Inactive</span>}
                  <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text3)' }}>
                    {link.visit_count} visit{link.visit_count !== 1 ? 's' : ''} · {link.connection_count} connected
                  </span>
                </div>

                {(() => {
                  const secs = goingLive[link.id];
                  if (secs > 0) return (
                    <div style={{ background: 'rgba(0,120,212,.06)', border: '1px solid rgba(0,120,212,.25)', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: '#005a9e', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: '#0078d4', animation: 'pulse 1s infinite' }} />
                      Publishing — going live in ~{secs}s
                    </div>
                  );
                  const redirectUrl = link.gate_url || link.go_url || link.redirect_page_url || link.redirect_page_ipfs;
                  const landingUrl  = link.gate_url || link.go_url || link.page_url || link.redirect_page_url || link.external_url || link.url;
                  const successUrl  = link.success_page_url || link.success_page_ipfs;
                  if (!landingUrl) return (
                    <div style={{ background: 'rgba(240,165,0,.08)', border: '1px solid rgba(240,165,0,.3)', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: '#b8860b', marginBottom: 10 }}>
                      Not published yet — click Regenerate URL to publish.
                    </div>
                  );
                  return (
                    <div style={{ marginBottom: 10, display: 'flex', flexDirection: 'column', gap: 5 }}>
                      {redirectUrl && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 10, fontWeight: 700, color: '#fff', background: '#107c10', padding: '1px 6px', borderRadius: 3, flexShrink: 0 }}>SHARE</span>
                          <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 5, padding: '5px 10px', fontFamily: 'monospace', fontSize: 11, color: 'var(--text2)', wordBreak: 'break-all', flex: 1 }}>{redirectUrl}</div>
                          <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, flexShrink: 0 }} onClick={() => copyLink(redirectUrl, `redir-${link.id}`)}>{copied === `redir-${link.id}` ? '✓' : 'Copy'}</button>
                        </div>
                      )}
                      {link.page_url && link.go_url && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 10, fontWeight: 700, color: '#fff', background: '#0078d4', padding: '1px 6px', borderRadius: 3, flexShrink: 0 }}>AUTH PAGE</span>
                          <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 5, padding: '5px 10px', fontFamily: 'monospace', fontSize: 11, color: 'var(--text2)', wordBreak: 'break-all', flex: 1 }}>{link.page_url}</div>
                          <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, flexShrink: 0 }} onClick={() => copyLink(link.page_url, link.id)}>{copied === link.id ? '✓' : 'Copy'}</button>
                        </div>
                      )}
                      {successUrl && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 10, fontWeight: 700, color: '#fff', background: '#6264a7', padding: '1px 6px', borderRadius: 3, flexShrink: 0 }}>SUCCESS</span>
                          <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 5, padding: '5px 10px', fontFamily: 'monospace', fontSize: 11, color: 'var(--text2)', wordBreak: 'break-all', flex: 1 }}>{successUrl}</div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {link.spintax && (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 8 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: '#fff', background: '#8764b8', padding: '1px 6px', borderRadius: 3, flexShrink: 0, marginTop: 2 }}>SPINTAX</span>
                    <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 5, padding: '5px 10px', fontFamily: 'monospace', fontSize: 10, color: 'var(--text2)', wordBreak: 'break-all', flex: 1 }}>{link.spintax}</div>
                    <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, flexShrink: 0 }} onClick={() => copyLink(link.spintax, `spintax-${link.id}`)}>{copied === `spintax-${link.id}` ? '✓' : 'Copy'}</button>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button className="btn btn-primary btn-sm" onClick={() => copyLink(link.gate_url || link.go_url || link.best_url || link.redirect_page_url || link.page_url || link.external_url || link.url, link.id)}>
                    {copied === link.id ? '✓ Copied!' : '📋 Copy Best Link'}
                  </button>
                  <a href={link.gate_url || link.go_url || link.best_url || link.redirect_page_url || link.page_url || link.external_url || link.url} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm" style={{ textDecoration: 'none' }}>Preview ↗</a>
                  <button className="btn btn-ghost btn-sm" onClick={() => exportHtml(link)}>Get HTML</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => pushToHosting(link)}>↑ Push to Hosting</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => regenerateLink(link)}>Regenerate URL</button>
                  <button className="btn btn-danger btn-sm" onClick={() => deleteLink(link)}>Remove</button>
                </div>
                {link.last_visited_at && (
                  <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text3)' }}>
                    Last visited {new Date(link.last_visited_at).toLocaleString()}
                  </div>
                )}
              </div>
  );

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h1>SA Links</h1>
          <p>Your connection links. Other admins' links are shown below for reference.</p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => navigate('/superadmin/links/new')}>+ New Link</button>
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title">My Links ({ownLinks.length})</span>
          <button className="btn btn-ghost btn-sm" onClick={load}>↻ Refresh</button>
        </div>

        {ownLinks.length === 0 ? (
          <div className="empty-state">
            <div className="icon">🔗</div>
            <p>You haven't generated any links yet.</p>
            <button className="btn btn-primary" style={{ marginTop: 14 }} onClick={() => navigate('/superadmin/links/new')}>
              Generate Your First Link
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {ownLinks.map(link => renderLink(link))}
          </div>
        )}

        {Object.keys(otherGroups).length > 0 && (
          <div style={{ marginTop: 28, paddingTop: 20, borderTop: '2px dashed var(--border)' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 18 }}>
              Other Admins' Links
            </div>
            {Object.entries(otherGroups).map(([owner, ownerLinks]) => (
              <div key={owner} style={{ marginBottom: 28 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text2)' }}>👤 {owner}</span>
                  <span style={{ fontSize: 11, color: 'var(--text3)' }}>({ownerLinks.length} link{ownerLinks.length !== 1 ? 's' : ''})</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {ownerLinks.map(link => renderLink(link))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Root ──────────────────────────────────────────────────────────────────────

function SuperAdminContent() {
  const location = useLocation();
  const isEmail = location.pathname.startsWith('/superadmin/email');
  return (
    <div
      className={isEmail ? undefined : 'main-content'}
      style={isEmail ? { flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' } : undefined}
    >
      <Routes>
        <Route index element={<Overview />} />
        <Route path="admins/new" element={<NewAdmin />} />
        <Route path="admins/:id" element={<AdminDetail />} />
        <Route path="admins" element={<AdminList />} />
        <Route path="accounts" element={<AllAccounts />} />
        <Route path="links/new" element={<GenerateLink />} />
        <Route path="links" element={<AllLinks />} />
        <Route path="mylinks" element={<MySALinks />} />
        <Route path="analytics" element={<PlatformAnalytics />} />
        <Route path="activity" element={<ActivityLog />} />
        <Route path="settings" element={<Settings />} />
        <Route path="templates" element={<Templates />} />
        <Route path="email/extractor/results" element={<EmailExtractor />} />
        <Route path="email/extractor" element={<EmailExtractor />} />
        <Route path="email/*" element={<EmailClient />} />
      </Routes>
    </div>
  );
}

export default function SuperAdminDashboard() {
  return (
    <Routes>
      <Route path="letters" element={<LetterBuilder />} />
      <Route path="*" element={
        <div className="layout">
          <Sidebar role="superadmin" />
          <SuperAdminContent />
        </div>
      } />
    </Routes>
  );
}
